import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import ArtistSidebar from '../../components/artist/ArtistSidebar';
import { useArtistAuth } from '../../contexts/ArtistAuthContext';
import { useMessageCount } from '../../contexts/MessageCountContext';
import { useEventChatUnreadDivider } from '../../components/common/chat/timeline/chatUnreadDivider';
import { ADMIN_MESSAGE_FORMATS } from '../../constants/messageFormats';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import '../../css/artist/chats/ArtistEventChatPage.css';
import '../../css/common/chat/ChatTargetListRow.css';
import ArtistMemberListModal from '../../components/artist/chats/ArtistMemberListModal';
import ArtistEventNotesPanel from '../../components/artist/chats/ArtistEventNotesPanel';
import ChatComposer from '../../components/common/chat/composer/ChatComposer';
import ChatQuestionPendingBar from '../../components/common/chat/questions/ChatQuestionPendingBar';
import ChatImageModal from '../../components/common/chat/composer/ChatImageModal';
import { shouldIgnorePopStateForHistoryDismiss } from '../common/useHistoryDismiss';
import EventFlyerMedia from '../../components/common/EventFlyerMedia';
import { getFileIconClass } from '../../components/common/chat/composer/fileIconClass';
import {
  formatDateTimeJST,
  formatDateJP,
  toDateInput,
  sidebarTimeLabelJST,
  toTimeInput,
  isWithinHoursFromNow,
  formatItemCount,
} from '../../utils/datetime';
import { useToast } from '../../components/common/ToastSystem';
import { useMaintenance, MAINTENANCE_MODES } from '../../contexts/MaintenanceContext';
import useChatDragDrop from '../../components/common/chat/composer/useChatDragDrop';
import ChatDragOverlay from '../../components/common/chat/composer/ChatDragOverlay';
import ChatEmptyState from '../../components/common/chat/shared/ChatEmptyState';
import EventMessageRows from '../../components/common/chat/timeline/EventMessageRows';
import ChatTargetSidebarLoading from '../../components/common/chat/sidepanel/ChatTargetSidebarLoading';
import ChatTargetListRow from '../../components/common/chat/sidepanel/ChatTargetListRow';
import { ArtistEventChatGuidanceMessage } from '../../components/common/chat/timeline/ArtistChatGuidanceSystemMessages';
import ChatMentionTaskNotice from '../../components/common/chat/mentions/ChatMentionTaskNotice';
import { renderChatSearchHighlight } from '../../components/common/chat/search/chatSearchHighlight';
import ChatMentionTaskResolutionModal from '../../components/common/chat/mentions/ChatMentionTaskResolutionModal';
import useChatMentionTaskResolution from '../../components/common/chat/mentions/useChatMentionTaskResolution';
import ChatAreaHeader from '../../components/common/chat/shared/ChatAreaHeader';
import ChatHeaderIconButton from '../../components/common/chat/shared/ChatHeaderIconButton';
import EventNoteActionButton from '../../components/common/chat/notes/EventNoteActionButton';
import ChatAnnouncementBar from '../../components/common/chat/announcements/ChatAnnouncementBar';
import ChatAnnouncementListModal from '../../components/common/chat/announcements/ChatAnnouncementListModal';
import ChatMessageSelectableCopyArea from '../../components/common/chat/timeline/ChatMessageSelectableCopyArea';
import ChatMessageReactionBar from '../../components/common/chat/reactions/ChatMessageReactionBar';
import ChatMessageMenuReactionPicker from '../../components/common/chat/reactions/ChatMessageMenuReactionPicker';
import ChatMessageReactionDetailModal from '../../components/common/chat/reactions/ChatMessageReactionDetailModal';
import ChatReplySnippet from '../../components/common/chat/composer/ChatReplySnippet';
import useChatNotesBadgeCount from '../../components/common/chat/notes/useChatNotesBadgeCount';
import useChatAnnouncements from '../../components/common/chat/announcements/useChatAnnouncements';
import useEventChatAssetBadges from '../../components/common/chat/shared/useEventChatAssetBadges';
import useDelayedVisibility from '../../components/common/chat/shared/useDelayedVisibility';
import useChatMessageReactions from '../../components/common/chat/reactions/useChatMessageReactions';
import useViewportBoundedMenuPosition from '../../components/common/chat/shared/useViewportBoundedMenuPosition';
import { focusChatAnchorMessage } from '../../components/common/chat/timeline/chatMessageAnchorUtils';
import { supportsChatMessageReaction } from '../../components/common/chat/reactions/chatMessageReactionUtils';
import {
  hasCopyableChatMessageText,
  resolveCopyableChatMessageText,
  writeTextToClipboard,
} from '../../components/common/chat/timeline/chatMessageCopyUtils';
import { buildImageGroupDisplayMessages } from '../../components/common/chat/composer/chatImageGroupUtils';
import {
  buildToolbarFilterChips,
  formatSearchChipDate,
} from '../../components/common/chat/search/chatTargetSearchChipUtils';
import {
  patchMessageReadCountsByIds,
  pickRealtimeReadCount,
} from '../../components/common/chat/reactions/readReceiptRealtimeUtils';
import { createEventAvatarRenderers } from '../../components/common/chat/avatars/eventAvatarRenderers';
import ChatProfileModalLayer from '../../components/common/chat/members/ChatProfileModalLayer';
import {
  buildEventAnnouncementChecks,
  parseEventTimetableRuntimePayload,
  resolveEventTimetableRuntimeVisibility,
} from '../../components/common/chat/timeline/eventSystemMessageUtils';
import {
  buildEventAdminParticipants,
  buildEventArtistParticipants,
  buildEventConciergeParticipants,
  groupEventParticipantsByPart,
  resolveEventScopedPartMeta,
} from '../../components/common/chat/members/eventParticipantsPanelDataUtils';
import useEventParticipantPartScope from '../../components/common/chat/members/useEventParticipantPartScope';
import {
  resolveEventAnnouncementIconClass as getAnnouncementIconClass,
  isEventAnnouncementMessage as isAnnouncementMessage,
  isEventFinishedMessage,
  isEventImagesAddedMessage,
  isEventInfoPublishedMessage,
  isEventInfoUpdatedMessage,
  isEventMaterialSharedMessage,
  isEventMembersPublishedMessage,
  isStageSeatingSharedMessage,
  isEventNoteCreateMessage as isNoteCreateMessage,
  isEventNoteDeleteMessage as isNoteDeleteMessage,
  isEventNoteMessage as isNoteMessage,
  isEventNoteUpdateMessage as isNoteUpdateMessage,
  isEventSimpleAnnouncementMessage as isSimpleAnnouncementMessage,
  resolveEventSidebarPreview as resolveSidebarPreview,
  isEventTimetableCreatedMessage as isTimetableCreatedMessage,
  isEventTimetableMemoMessage as isTimetableMemoMessage,
  isEventTimetableRuntimeMessage as isTimetableRuntimeMessage,
  isEventTimetableUpdatedMessage as isTimetableUpdatedMessage,
} from '../../components/common/chat/timeline/eventMessageFormatUtils';
import {
  resolveEventAnnouncementSenderName as getAnnouncementSenderName,
  resolveEventMessageSourceBadgeLabel as resolveMessageSourceBadgeLabel,
  shouldShowEventMessageSourceBadge,
} from '../../components/common/chat/timeline/eventMessageSourceUtils';
import { createEventMessageRenderPipeline } from '../../components/common/chat/timeline/eventMessageRenderPipeline';
import { createEventPipelineOptions } from '../../components/common/chat/eventlist/eventPipelineConfigUtils';
import { createEventParticipantsPanelProps } from '../../components/common/chat/members/eventParticipantsPanelConfigUtils';
import useChatProfileModalState from '../../components/common/chat/members/useChatProfileModalState';
import {
  EVENT_CHAT_MODE_GROUP_UNIFIED as CHAT_MODE_GROUP_UNIFIED,
  isUnifiedEventGroupChatRow,
  normalizeEventPerformanceTypeForThread,
  resolveExplicitEventChatMode,
  resolveExplicitEventGroupCount,
  resolveEventChatMode,
  resolveEventGroupCount,
  resolveEventThreadId,
} from '../../components/common/chat/eventlist/eventThreadMetaUtils';
import { clearActiveChatContext, setActiveChatContext } from '../../utils/activeChatContext';
import { formatAdminDisplayName } from '../../utils/adminDisplayName';
import {
  appendVariant,
} from '../../components/common/chat/composer/chatAssetUtils';
import {
  listArtistPendingMentionTargets,
  resolveArtistMentionTarget,
} from '../../api/artistChat';
import {
  deleteEventChatMessage,
  markEventChatMessagesRead,
  sendEventChatFileMessage,
  sendEventChatImageMessages,
} from '../../api/eventChat';
import {
  listCommonEventCastingProgress,
  listEventCastingInfo,
} from '../../api/eventDetails';
import {
  normalizeEventUuidList,
  normalizePartLabel,
} from '../../components/common/chat/members/memberEventPartUtils';
import {
  parsePartIds,
  pickFirstTrimmedString as pickFirstString,
  toNum,
} from '../../components/common/chat/members/memberListUtils';
import {
  buildParticipantEventMembershipLabel as buildEventParticipantEventMembershipLabel,
  buildParticipantEventPartSummary as buildEventParticipantEventPartSummary,
} from '../../components/common/chat/members/eventParticipantUtils';
import {
  buildEventAdminProfilePayload,
  buildEventArtistProfilePayload,
  createEventProfilePayloadBuilders,
} from '../../components/common/chat/members/eventProfilePayloadUtils';
import { createEventAvatarProfileTargetResolver } from '../../components/common/chat/avatars/eventAvatarProfileTargetUtils';
import {
  buildEventScheduleFromInfo,
  buildGroupEventDetailRows,
  buildHeaderNextMultiPerformance,
  buildHeaderNextPerformance,
  buildUnifiedEventHeaderSchedule,
  resolveHeaderFlyerSource,
} from '../../components/common/chat/sidepanel/eventSidePanelUtils';
import {
  mergeEventSidebarRow,
  normalizeGroupEventRows,
  parseVisibilityFlag,
  resolveEventVisibilityFromGroup,
} from '../../components/common/chat/eventlist/eventGroupEventUtils';
import {
  normalizeServerMessage,
  statusToDisplayMessage,
} from '../../components/common/chat/eventlist/eventRequestUtils';
import { joinEventRooms } from '../../components/common/chat/eventlist/eventSocketRoomUtils';
import {
  applySearchResultEvents,
  resolveEventListModeState,
} from '../../components/common/chat/eventlist/eventListStateUtils';
import { fetchEventUnreadMap } from '../../components/common/chat/eventlist/eventUnreadUtils';
import {
  buildRealtimeEventSidebarPatch,
  upsertEventSidebarRowByUuid,
} from '../../components/common/chat/sidepanel/eventSidebarRowStateUtils';
import {
  composeArtistEventRowWithComposite,
  mapRowsByEventUuid,
} from '../../components/common/chat/eventlist/eventListComposeUtils';
import { loadEventListRows } from '../../components/common/chat/eventlist/eventListFetchUtils';
import {
  loadMoreEventList,
  observeEventListLoadMoreScroll,
  observeEventListLoadMoreSentinel,
  scheduleEventListRefreshTop,
  shouldLoadMoreEventList,
  triggerEventListLoadMoreIfNeeded,
} from '../../components/common/chat/eventlist/eventListInteractionUtils';
import {
  cleanupTimeoutRefs,
  shouldMarkLatestAsRead,
} from '../../components/common/chat/eventlist/eventChatEffectUtils';
import {
  applyEventTargetSearchDraft,
  clearEventTargetSearch,
  countActiveEventSearchFilters,
  loadEventSearchRowsWithUnread,
  openEventTargetSearchModal,
  resolveEventSearchCriteria,
} from '../../components/common/chat/search/eventSearchUtils';
import { linkifyTextNodes as linkifyText } from '../../components/common/chat/timeline/linkifyTextNodes';
import {
  buildChatMentionCandidates,
  normalizeMentionTargetId,
  normalizeMentionTargetRole,
  normalizeMentionTargetType,
} from '../../components/common/chat/mentions/chatMentionUtils';
import ChatTargetSidebarToolbar from '../../components/common/chat/sidepanel/ChatTargetSidebarToolbar';
import ChatWorkspaceLayout from '../../components/common/chat/shared/ChatWorkspaceLayout';
import '../../css/common/chat/ChatDragDrop.css';
import { ellipsizeFileName } from '../../components/common/chat/composer/fileNameUtils';
import {
  ArtistEventChatSearchModal,
  ArtistEventAlbumModal,
  ArtistEventChatSidePanel,
  ArtistEventMaterialsModal,
} from '../../components/artist/chats/artistEventChatPageModals';
import ChatLinksModal from '../../components/common/chat/assets/ChatLinksModal';
import ChatMediaModal from '../../components/common/chat/assets/ChatMediaModal';
import { eventChatAssetSource } from '../../api/chatAssets';

import {
  API_URL,
  MESSAGE_FILE_PATH,
  buildArtistEventNotesListUrl,
  ADMIN_PROFILE_IMAGE_PATH,
  ARTIST_PROFILE_IMAGE_PATH,
  EARLIEST_CREATED_AT,
  STALE_MS,
  UNSENDABLE_FORMATS,
  dbg,
  ARTIST_EVENTS_HEAD_URL,
  ARTIST_EVENTS_PAGE_URL,
  ARTIST_SIDEBAR_ROW_URL,
  ARTIST_PARTICIPANTS_URL,
  ARTIST_GROUP_EVENTS_URL,
  ARTIST_REPRESENTATIVE_EVENT_BY_THREAD_URL,
  EVENT_TARGET_ROW_CLASSES,
  ARTIST_SEARCH_API,
  buildAssetUrl,
  buildFlyerUrl,
  buildMessageImageUrl,
  applyLandscapeContain,
  formatExpireDateLabel,
  socket,
  fetchJsonSafe,
  MS_DAY,
  endOfDayJst,
  handleDownload,
  ACCESS_DENIED_STATUSES,
  MAX_FILE_BYTES,
  MAX_IMAGE_COUNT,
} from '../../components/artist/chats/artistEventChatPageHelpers';
import { mergeMessages, patchMessages, removeMessages } from '../../components/common/chat/timeline/timelineMerge';
import useArtistEventChatParticipants from './useArtistEventChatPageElement.participants';
import useArtistEventChatListState from './useArtistEventChatPageElement.list';
import useArtistEventChatMessages from './useArtistEventChatPageElement.messages';
import useArtistEventChatRuntime from './useArtistEventChatPageElement.runtime';
import useArtistEventChatMobileView, { readChatQuestionMessageId } from './useArtistEventChatPageElement.mobile';
export default function useArtistEventChatPageElement() {
  const ACTIVE_CHAT_OWNER = 'artist:event-chat';
  const mobileBackButtonClassName = 'chat-header-back-btn';
  const { error: toastError, success: toastSuccess } = useToast();
  const { maintenance, maintenanceMode } = useMaintenance();
  const isReadOnly =
    maintenanceMode === MAINTENANCE_MODES.READ_ONLY || (maintenance && maintenance.readOnly);
  const location = useLocation();
  const navigate = useNavigate();
  const { artistUser } = useArtistAuth();
  const loggedInArtistId = artistUser?.userId;
  const { eventUnreadMapArtist, refreshEventChatUnread } = useMessageCount();

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventInfo, setSelectedEventInfo] = useState(null);
  const [showMaterialsModal, setShowMaterialsModal] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [chatHeaderMenuOpen, setChatHeaderMenuOpen] = useState(false);
  const [chatHeaderMenuPosition, setChatHeaderMenuPosition] = useState(null);
  const [pendingMentionTargets, setPendingMentionTargets] = useState({});
  const [resolvingMentionTargetId, setResolvingMentionTargetId] = useState('');
  const {
    materialBadge,
    albumBadge,
    hasNew: hasNewEventAssets,
    hasAny: hasAnyEventAssets,
    markMaterialsSeen,
    markAlbumSeen,
  } = useEventChatAssetBadges({
    eventId: selectedEventId,
    viewerKey: `artist:${loggedInArtistId || 'unknown'}`,
  });
  const assetMenuBadgeTone = hasNewEventAssets ? 'new' : hasAnyEventAssets ? 'has' : 'none';
  const openMaterialsModal = useCallback(() => {
    markMaterialsSeen();
    setShowMaterialsModal(true);
  }, [markMaterialsSeen]);
  const openAlbumModal = useCallback(() => {
    markAlbumSeen();
    setShowAlbumModal(true);
  }, [markAlbumSeen]);
  const openLinksModal = useCallback(() => setShowLinksModal(true), []);
  const openMediaModal = useCallback(() => setShowMediaModal(true), []);
  const chatAssetSource = useMemo(
    () => (selectedEventId ? eventChatAssetSource(selectedEventId) : null),
    [selectedEventId],
  );

  const closeChatHeaderMenu = useCallback(() => {
    setChatHeaderMenuOpen(false);
    setChatHeaderMenuPosition(null);
  }, []);
  const toggleChatHeaderMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (chatHeaderMenuOpen) {
      closeChatHeaderMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    setChatHeaderMenuPosition({
      left: Math.min(Math.max(12, rect.right - menuWidth), window.innerWidth - menuWidth - 12),
      top: Math.min(rect.bottom + 8, window.innerHeight - 96),
    });
    setChatHeaderMenuOpen(true);
  }, [chatHeaderMenuOpen, closeChatHeaderMenu]);
  useEffect(() => {
    if (!chatHeaderMenuOpen) return undefined;
    const close = () => closeChatHeaderMenu();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [chatHeaderMenuOpen, closeChatHeaderMenu]);

  const refreshPendingMentionTargets = useCallback(async () => {
    try {
      const data = await listArtistPendingMentionTargets({ scope: 'event' });
      const next = {};
      (Array.isArray(data?.targets) ? data.targets : []).forEach((target) => {
        const id = String(target?.targetId || '').trim();
        if (!id) return;
        next[id] = {
          count: Number(target?.count || 0),
          messageId: target?.messageId || target?.message_id || null,
          latestCreatedAt: target?.latestCreatedAt || null,
        };
      });
      setPendingMentionTargets(next);
    } catch {
      setPendingMentionTargets({});
    }
  }, []);

  const resolveMentionTarget = useCallback(async (targetId) => {
    const normalizedTargetId = String(targetId || '').trim();
    if (!normalizedTargetId) return;
    setResolvingMentionTargetId(normalizedTargetId);
    try {
      await resolveArtistMentionTarget({ scope: 'event', targetId: normalizedTargetId });
      setPendingMentionTargets((current) => {
        const next = { ...current };
        delete next[normalizedTargetId];
        return next;
      });
    } catch {
      toastError('メンション対応状態の更新に失敗しました。');
    } finally {
      setResolvingMentionTargetId('');
    }
  }, [toastError]);

  const updatePendingMentionTargetCount = useCallback((scope, targetId, delta) => {
    if (scope !== 'event') return;
    const normalizedTargetId = String(targetId || '').trim();
    if (!normalizedTargetId) return;
    setPendingMentionTargets((current) => {
      const currentTarget = current[normalizedTargetId];
      const nextCount = Math.max(0, Number(currentTarget?.count || 0) + Number(delta || 0));
      const next = { ...current };
      if (nextCount <= 0) {
        delete next[normalizedTargetId];
      } else {
        next[normalizedTargetId] = { ...currentTarget, count: nextCount };
      }
      return next;
    });
  }, []);

  const {
    modalProps: mentionResolutionModalProps,
    loadingTargetKey: loadingMentionResolutionTargetKey,
    openMentionResolution,
  } = useChatMentionTaskResolution({
    actorType: 'artist',
    onTargetCountChange: updatePendingMentionTargetCount,
    onError: toastError,
  });

  useEffect(() => {
    refreshPendingMentionTargets();
  }, [refreshPendingMentionTargets]);

  const {
    pendingRestoreRef,
    isMobile,
    mobileView,
    setMobileView,
    mobileEventListPrefRef,
    mobileEventChatHistoryRef,
  } = useArtistEventChatMobileView({
    URLSearchParams,
    location,
    selectedEventId,
  });

  // ===== イベント一覧（ページング・検索など） =====
  const EVENTS_PAGE_LIMIT = 20;
  const [events, setEvents] = useState([]);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [loadMoreNode, setLoadMoreNode] = useState(null);
  const [eventListNode, setEventListNode] = useState(null);
  const eventListLoadMorePendingRef = useRef(false);
  const eventListForceFallbackExhaustedRef = useRef(false);

  const [isBootLoading, setIsBootLoading] = useState(true);
  const [orderFrozen, setOrderFrozen] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // 検索結果は50件ずつ取得し、スクロールで過去分を追加読み込みする
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchNextOffset, setSearchNextOffset] = useState(0);
  const [searchSnapshotToken, setSearchSnapshotToken] = useState('');
  const [isSearchLoadingMore, setIsSearchLoadingMore] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchDraft, setSearchDraft] = useState({
    eventQuery: '',
    messageQuery: '',
    startDate: '',
    endDate: '',
  });

  const [, setGlobalError] = useState('');
  const [, setEventsError] = useState('');
  const [, setMessagesError] = useState('');
  const [, setDetailError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [fatalAccessError, setFatalAccessError] = useState(false);

  const displayedIdsRef = useRef(new Set());
  useEffect(() => { displayedIdsRef.current = new Set(events.map(e => e.event_uuid)); }, [events]);

  const [showNotes, setShowNotes] = useState(false);
  const { noteCount, refreshNoteCount } = useChatNotesBadgeCount({
    ownerId: selectedEventId,
    buildListUrl: buildArtistEventNotesListUrl,
    enabled: Boolean(selectedEventId),
  });
  const {
    announcements,
    latestAnnouncement,
    announcementCount,
    listOpen: isAnnouncementListOpen,
    setListOpen: setIsAnnouncementListOpen,
    refreshAnnouncements,
    pinAnnouncement,
    unpinAnnouncement,
    isAnnouncedMessage,
    actionMessageId: announcementActionMessageId,
  } = useChatAnnouncements({
    mode: 'artist',
    scope: 'event',
    eventId: selectedEventId || '',
    enabled: Boolean(selectedEventId),
    onError: toastError,
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const notesModeRef = useRef('list');

  // moved up
  const [sidebarDetails, setSidebarDetails] = useState(null);
  const [, setIsSidebarLoading] = useState(false);
  const [groupEventSidebarRows, setGroupEventSidebarRows] = useState([]);
  const [groupPanelEvents, setGroupPanelEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [replyDraft, setReplyDraft] = useState(null);
  const {
    detailOpen: isReactionDetailOpen,
    detailGroups: reactionDetailGroups,
    detailLoading: reactionDetailLoading,
    openReactionDetail,
    closeReactionDetail,
    toggleReaction,
    refreshMessageReactionDetails,
    isReactionPending,
  } = useChatMessageReactions({
    mode: 'artist',
    scope: 'event',
    eventId: selectedEventId || '',
    enabled: Boolean(selectedEventId),
    setMessages,
    onError: toastError,
  });
  const [messageMenu, setMessageMenu] = useState(null);
  const [inlineCopyMessageId, setInlineCopyMessageId] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [messageWindowOffset, setMessageWindowOffset] = useState(0);
  const [hasNewerMessages, setHasNewerMessages] = useState(false);
  const [loadingNewerMessages, setLoadingNewerMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [activeAnchorMessageId, setActiveAnchorMessageId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [loadedMessagesEventId, setLoadedMessagesEventId] = useState('');
  const showDelayedInitialLoading = useDelayedVisibility(isInitialLoading, 1000);
  const [latestCreatedAt, setLatestCreatedAt] = useState(null);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
    if (selectedEventId && pendingRestoreRef.current) {
      pendingRestoreRef.current = false;
    }
  }, [selectedEventId, pendingRestoreRef]);
  useEffect(() => {
    setReplyDraft(null);
  }, [selectedEventId]);
  useEffect(() => {
    showNotesRef.current = showNotes;
  }, [showNotes]);
  useEffect(() => {
    isSearchModeRef.current = isSearchMode;
  }, [isSearchMode]);
  useEffect(() => {
    latestCreatedAtRef.current = latestCreatedAt;
  }, [latestCreatedAt]);
  useEffect(
    () => () => {
      if (anchorHighlightTimerRef.current) {
        clearTimeout(anchorHighlightTimerRef.current);
        anchorHighlightTimerRef.current = null;
      }
    },
    [],
  );

  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [imgModalOpen, setImgModalOpen] = useState(false);
  const [imgModalIndex, setImgModalIndex] = useState(0);
  const imageModalHistoryRef = useRef({ pushed: false, closing: false });

  const imageMessages = useMemo(
    () => messages.filter((msg) => msg.message_format === ADMIN_MESSAGE_FORMATS.IMAGE && (msg.image_file_name || msg.image_url)),
    [messages],
  );
  const imageItems = useMemo(() => {
    const items = [];
    imageMessages.forEach((msg) => {
      const fullSrc = msg.image_url || buildMessageImageUrl(msg.image_file_name, null);
      const proxySrc = buildMessageImageUrl(msg.image_file_name, 'proxy') || fullSrc;
      if (!fullSrc) return;
      items.push({
        ...msg,
        fullSrc,
        proxySrc,
        src: proxySrc,
        messageId: msg.id,
        fileName: msg.image_file_name,
        downloadUrl: fullSrc,
      });
    });
    return items;
  }, [imageMessages]);
  const displayMessages = useMemo(
    () => buildImageGroupDisplayMessages(messages),
    [messages],
  );
  const selectedEventKey = String(selectedEventId || '').trim();
  const shouldShowChatGuidance = Boolean(
    selectedEventKey &&
      loadedMessagesEventId === selectedEventKey &&
      !isFetching &&
      !isInitialLoading &&
      !loadingOlderMessages &&
      !loadingNewerMessages &&
      !hasMore
  );
  const imageIndexByMessageId = useMemo(() => {
    const map = new Map();
    imageItems.forEach((item, idx) => {
      if (item?.messageId != null) map.set(item.messageId, idx);
    });
    return map;
  }, [imageItems]);

  useEffect(() => {
    if (!imgModalOpen) return;
    if (!imageItems.length) {
      setImgModalOpen(false);
      return;
    }
    if (imgModalIndex >= imageItems.length) setImgModalIndex(0);
  }, [imgModalOpen, imgModalIndex, imageItems.length]);

  const openImageModal = useCallback((messageId) => {
    const idx = imageIndexByMessageId.get(messageId);
    if (idx == null) return;
    setImgModalIndex(idx);
    setImgModalOpen(true);
    if (!imageModalHistoryRef.current.pushed) {
      try {
        const baseState =
          window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
        window.history.pushState({ ...baseState, chatImageModal: true }, '');
        imageModalHistoryRef.current.pushed = true;
        imageModalHistoryRef.current.closing = false;
      } catch {
        imageModalHistoryRef.current.pushed = false;
        imageModalHistoryRef.current.closing = false;
      }
    }
  }, [imageIndexByMessageId]);
  const closeImageModal = useCallback((viaPop = false) => {
    setImgModalOpen(false);
    if (!viaPop && imageModalHistoryRef.current.pushed) {
      imageModalHistoryRef.current.closing = true;
      try {
        window.history.back();
      } catch {
        imageModalHistoryRef.current.pushed = false;
        imageModalHistoryRef.current.closing = false;
      }
      return;
    }
    imageModalHistoryRef.current.pushed = false;
    imageModalHistoryRef.current.closing = false;
  }, []);
  const showPrevImage = useCallback(() => {
    setImgModalIndex((prev) => {
      if (!imageItems.length) return 0;
      return prev <= 0 ? imageItems.length - 1 : prev - 1;
    });
  }, [imageItems.length]);
  const showNextImage = useCallback(() => {
    setImgModalIndex((prev) => {
      if (!imageItems.length) return 0;
      return prev >= imageItems.length - 1 ? 0 : prev + 1;
    });
  }, [imageItems.length]);
  const [participantSnapshot, setParticipantSnapshot] = useState(null);

  const {
    selectedEventAdmins,
    currentEventMembersVisibleToArtists,
    membersVisibleToArtists,
    conciergePlanner,
    adminLookup,
    isSelectedGroupUnified,
    effectiveGroupEvents,
    groupEventDetailRows,
    buildParticipantEventMembershipLabel,
    buildParticipantEventPartSummary,
    eventSchedule,
    eventHeaderSchedule,
    headerPerformanceType,
    headerNextPerformance,
    isSelectedMultiPerformanceEvent,
    headerNextMultiPerformance,
    participantCounts,
    hasConciergeParticipants,
    scheduleDetail,
    partsList,
    setPartsList,
    setCastingInfo,
    setParticipantArtists,
    participantArtistMap,
    partsLookup,
    castingInfoMap,
    venueInfo,
    sidebarNote,
    selectedEventFlyerUrl,
    selectedEventFlyerBackUrl,
    selectedEventFlyerFullUrl,
    selectedEventFlyerBackFullUrl,
    eventDetailUrl,
    venueIdFromData,
    venueLink,
    venueResolvedAddress,
    venueResolvedName,
    mapLinkUrl,
    handleGoHeaderEventDetail,
    isUploading,
    setIsUploading,
    isSendLocked,
    setIsSendLocked,
    handleUploading,
    mentionCandidates,
    profileTarget,
    closeProfileModal,
    openProfileTarget,
    handleMentionClick,
    participantsPanelProps,
  } = useArtistEventChatParticipants({
    ADMIN_PROFILE_IMAGE_PATH,
    API_URL,
    ARTIST_PROFILE_IMAGE_PATH,
    CHAT_MODE_GROUP_UNIFIED,
    appendVariant,
    buildAssetUrl,
    buildChatMentionCandidates,
    buildEventAdminParticipants,
    buildEventAdminProfilePayload,
    buildEventArtistParticipants,
    buildEventArtistProfilePayload,
    buildEventConciergeParticipants,
    buildEventParticipantEventMembershipLabel,
    buildEventParticipantEventPartSummary,
    buildEventScheduleFromInfo,
    buildFlyerUrl,
    buildGroupEventDetailRows,
    buildHeaderNextMultiPerformance,
    buildHeaderNextPerformance,
    buildUnifiedEventHeaderSchedule,
    createEventParticipantsPanelProps,
    events,
    fetchJsonSafe,
    formatDateJP,
    groupEventParticipantsByPart,
    groupEventSidebarRows,
    groupPanelEvents,
    loggedInArtistId,
    navigate,
    normalizeEventUuidList,
    normalizeGroupEventRows,
    normalizeMentionTargetId,
    normalizeMentionTargetRole,
    normalizeMentionTargetType,
    normalizePartLabel,
    parseVisibilityFlag,
    participantSnapshot,
    pickFirstString,
    resolveEventChatMode,
    resolveEventGroupCount,
    resolveEventScopedPartMeta,
    resolveEventThreadId,
    resolveEventVisibilityFromGroup,
    resolveHeaderFlyerSource,
    selectedEventId,
    selectedEventInfo,
    setIsMemberModalOpen,
    sidebarDetails,
    toDateInput,
    toTimeInput,
    useChatProfileModalState,
    useEventParticipantPartScope,
  });

  const [, setUnreadMap] = useState({});

  const [showNewMsgBanner, setShowNewMsgBanner] = useState(false);
  const [bannerMsg, setBannerMsg] = useState(null);
  const [failedBannerAvatarUrl, setFailedBannerAvatarUrl] = useState('');
  useEffect(() => {
    setFailedBannerAvatarUrl('');
  }, [bannerMsg?.avatarUrl]);
      const bannerTimeoutRef = useRef(null);
      const lastBannerIdRef = useRef(null);
      const reconcileTimerRef = useRef(null);
    const longPressTimerRef = useRef(null);
  const RECON_LIMIT = 50;
  const messageIdsRef = useRef(new Set());
  const initialLoadRef = useRef(new Set());
  const pendingReadReceiptKeysRef = useRef(new Set());
  const completedReadReceiptKeysRef = useRef(new Set());
  const messagesRef = useRef([]);
  const unreadDividerMessageId = useEventChatUnreadDivider(selectedEventId, eventUnreadMapArtist, messages);
  const selectedEventIdRef = useRef(null);
  const selectedThreadIdRef = useRef(null);
  const showNotesRef = useRef(false);
  const isSearchModeRef = useRef(false);
  const latestCreatedAtRef = useRef(null);
  const fetchMessagesRef = useRef(null);
  const fetchNewMessagesRef = useRef(null);
  const getMainPartLabelRef = useRef(null);
  const anchorHighlightTimerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const currentRoomRef = useRef(null);
  const joinedRoomsRef = useRef(new Set());

  const prevUnreadMapRef = useRef(null);
  const refreshTopTimerRef = useRef(null);
  const lastDiffFetchRef = useRef(Date.now());

  const eventDetailCacheRef = useRef(new Map());
  const eventDetailInFlightRef = useRef(new Map());

  const limit = 20;

  const resolveThreadIdByEvent = useCallback((eventUuid) => {
    if (!eventUuid) return null;
    if (selectedEventInfo?.event_uuid === eventUuid) {
      const threadId = resolveEventThreadId(selectedEventInfo);
      if (threadId) return threadId;
    }
    if (sidebarDetails?.event_uuid === eventUuid) {
      const threadId = resolveEventThreadId(sidebarDetails);
      if (threadId) return threadId;
    }
    const row = (events || []).find((item) => item?.event_uuid === eventUuid);
    return resolveEventThreadId(row);
  }, [events, selectedEventInfo, sidebarDetails]);

  useEffect(() => {
    selectedThreadIdRef.current = resolveThreadIdByEvent(selectedEventId || selectedEventIdRef.current);
  }, [selectedEventId, resolveThreadIdByEvent]);

  useEffect(() => {
    if (selectedEventId) {
      setActiveChatContext({
        owner: ACTIVE_CHAT_OWNER,
        scope: 'artist',
        channel: 'event',
        eventId: selectedEventId,
        threadId: selectedThreadIdRef.current || resolveThreadIdByEvent(selectedEventId),
      });
      return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
    }
    clearActiveChatContext(ACTIVE_CHAT_OWNER);
    return () => clearActiveChatContext(ACTIVE_CHAT_OWNER);
  }, [selectedEventId, resolveThreadIdByEvent]);

  const nowJstDbString = () => {
    const fmt = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
  };

  const isViewingLatest = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return true;
    const nearBottomNormal = (c.scrollHeight - c.clientHeight - c.scrollTop) <= 80;
    const nearBottomReverse = Math.abs(c.scrollTop) <= 80;
    return nearBottomNormal || nearBottomReverse;
  }, []);

  const canUnsendMessage = useCallback(
    (message) => {
      if (isReadOnly) return false;
      if (!message) return false;
      const owns =
        message.isMyArtistMsg ||
        (message.message_type === 'artist_message' &&
          String(message.artist_id) === String(loggedInArtistId));
      if (!owns) return false;
      if (!UNSENDABLE_FORMATS.has(message.message_format)) return false;
      return isWithinHoursFromNow(message.created_at, 1);
    },
    [isReadOnly, loggedInArtistId],
  );

  const canAnnouncementMessage = useCallback((message) => {
    if (!message) return false;
    return UNSENDABLE_FORMATS.has(message.message_format);
  }, []);

  const canReactMessage = useCallback((message) => {
    if (!message || !supportsChatMessageReaction(message)) return false;
    return !Boolean(message?.isMyArtistMsg);
  }, []);

  const canReplyMessage = useCallback((message) => {
    if (!message) return false;
    const type = String(message.message_type || '').trim().toLowerCase();
    const format = String(message.message_format || '').trim().toLowerCase();
    return ['admin_message', 'artist_message'].includes(type) && ['text', 'image', 'file'].includes(format);
  }, []);

  const canCopyMessage = useCallback((message) => hasCopyableChatMessageText(message), []);

  const canOpenMessageMenu = useCallback(
    (message) =>
      canCopyMessage(message) ||
      canReplyMessage(message) ||
      canUnsendMessage(message) ||
      canAnnouncementMessage(message) ||
      canReactMessage(message),
    [canAnnouncementMessage, canCopyMessage, canReactMessage, canReplyMessage, canUnsendMessage],
  );

  const { menuRef: messageMenuRef, resolveMenuPosition } = useViewportBoundedMenuPosition({
    menu: messageMenu,
    setMenu: setMessageMenu,
    padding: 12,
    fallbackWidth: 248,
    fallbackHeight: 360,
  });

  const closeMessageMenu = useCallback(() => {
    setMessageMenu(null);
  }, []);

  const closePartialCopyMode = useCallback(() => {
    setInlineCopyMessageId('');
  }, []);

  const openMessageMenuAt = useCallback(
    (message, x, y) => {
      if (!message) return;
      const pos = resolveMenuPosition(x, y);
      setMessageMenu({ message, x: pos.x, y: pos.y });
    },
    [resolveMenuPosition],
  );

  const clearMessageLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMessageContextMenu = useCallback(
    (event, message) => {
      if (!canOpenMessageMenu(message)) return;
      event.preventDefault();
      openMessageMenuAt(message, event.clientX, event.clientY);
    },
    [canOpenMessageMenu, openMessageMenuAt],
  );

  const handleMessageTouchStart = useCallback(
    (event, message) => {
      if (!isMobile || !canOpenMessageMenu(message)) return;
      clearMessageLongPress();
      const touch = event.touches?.[0];
      if (!touch) return;
      const { clientX, clientY } = touch;
      longPressTimerRef.current = setTimeout(() => {
        openMessageMenuAt(message, clientX, clientY);
      }, 550);
    },
    [isMobile, clearMessageLongPress, openMessageMenuAt, canOpenMessageMenu],
  );

  const handleUnsendMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target || !selectedEventId) return;
    closeMessageMenu();
    try {
      const data = await deleteEventChatMessage(selectedEventId, target.id);
      if (data.success === false) {
        throw new Error(data?.message || data?.error || '送信取り消しに失敗しました。');
      }
      const deletedIds = Array.isArray(data?.messageIds) && data.messageIds.length
        ? data.messageIds.map((id) => String(id))
        : [String(target.id)];
      setMessages((prev) => removeMessages(prev, deletedIds));
    } catch (err) {
      toastError && toastError(err?.message || '送信取り消しに失敗しました。');
    }
  }, [messageMenu, closeMessageMenu, selectedEventId, toastError]);

  const handleToggleAnnouncementMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    try {
      if (isAnnouncedMessage(target.id)) {
        await unpinAnnouncement(target.id);
      } else {
        await pinAnnouncement(target.id);
      }
    } catch (_) {}
  }, [closeMessageMenu, isAnnouncedMessage, messageMenu, pinAnnouncement, unpinAnnouncement]);

  const handleMessageMenuReactionSelect = useCallback(async (messageId, reactionType) => {
    closeMessageMenu();
    try {
      await toggleReaction(messageId, reactionType);
    } catch (_) {}
  }, [closeMessageMenu, toggleReaction]);

  const handleCopyMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    closeMessageMenu();
    if (!copyText) {
      toastError?.('コピーできる本文がありません。');
      return;
    }
    try {
      await writeTextToClipboard(copyText);
      toastSuccess?.('メッセージをコピーしました。');
    } catch (error) {
      toastError?.(error?.message || 'コピーに失敗しました。');
    }
  }, [closeMessageMenu, messageMenu, toastError, toastSuccess]);

  const handleOpenInlinePartialCopy = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    closeMessageMenu();
    if (!copyText) {
      toastError?.('コピーできる本文がありません。');
      return;
    }
    const nextMessageId = String(target.id || '').trim();
    setInlineCopyMessageId((current) => (current === nextMessageId ? '' : nextMessageId));
  }, [closeMessageMenu, messageMenu, toastError]);

  const handleCopySelectionText = useCallback(async (selectedText) => {
    if (!String(selectedText || '').trim()) {
      toastError?.('コピーしたい範囲を選択してください。');
      return;
    }
    try {
      await writeTextToClipboard(selectedText);
      toastSuccess?.('選択範囲をコピーしました。');
    } catch (error) {
      toastError?.(error?.message || 'コピーに失敗しました。');
    }
  }, [toastError, toastSuccess]);

  const renderPartialCopyContent = useCallback((message, contentNode, isSelfMessage = false) => {
    const normalizedMessageId = String(message?.id || '').trim();
    if (!normalizedMessageId || !canCopyMessage(message)) return contentNode;
    return (
      <ChatMessageSelectableCopyArea
        active={inlineCopyMessageId === normalizedMessageId}
        messageId={normalizedMessageId}
        copyButtonSide={isSelfMessage ? 'left' : 'right'}
        onDeactivate={closePartialCopyMode}
        onCopySelection={handleCopySelectionText}
      >
        {contentNode}
      </ChatMessageSelectableCopyArea>
    );
  }, [canCopyMessage, closePartialCopyMode, handleCopySelectionText, inlineCopyMessageId]);

  const buildReplyDraftFromMessage = useCallback((message) => {
    if (!message) return null;
    const format = String(message.message_format || '').trim().toLowerCase();
    return {
      messageId: String(message.id || '').trim(),
      senderName:
        message.message_type === 'admin_message'
          ? String(message.admin_display_name || message.admin_full_name || '管理者').trim()
          : String(message.artist_name || artistUser?.name || artistUser?.username || 'アーティスト').trim(),
      type: format,
      previewText: format === 'text' ? String(message.content || '').trim() : '',
      fileName:
        format === 'file'
          ? String(message.original_name || message.file_file_name || '').trim()
          : '',
    };
  }, [artistUser]);

  const handleSelectReplyMessage = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    setReplyDraft(buildReplyDraftFromMessage(target));
  }, [buildReplyDraftFromMessage, closeMessageMenu, messageMenu]);

  const handleSelectAnnouncementMessage = async (messageId) => {
    const normalizedMessageId = String(messageId || '').trim();
    if (!selectedEventId || !normalizedMessageId) return;
    setIsAnnouncementListOpen(false);
    await fetchMessages(selectedEventId, true, 0, { anchorMessageId: normalizedMessageId });
  };

  const handleJumpToReplyMessage = useCallback(async (reply) => {
    const normalizedMessageId = String(reply?.messageId || '').trim();
    if (!selectedEventId || !normalizedMessageId) return;
    if (reply?.isUnavailable) {
      toastError('返信元メッセージが見つかりません。');
      return;
    }
    await fetchMessagesRef.current?.(selectedEventId, true, 0, { anchorMessageId: normalizedMessageId });
  }, [selectedEventId, toastError]);

  const isNearTopForPaging = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return false;
    const dir = (getComputedStyle(c).flexDirection || '').toLowerCase();
    const THRESH = 80;
    if (dir.includes('column-reverse')) {
      const maxAbs = c.scrollHeight - c.clientHeight;
      const absTop = Math.abs(c.scrollTop);
      return absTop >= (maxAbs - THRESH);
    } else {
      return c.scrollTop <= THRESH;
    }
  }, []);

  const postEventReadReceipt = useCallback(async (eventId, lastReadMessageId) => {
    if (!eventId || !lastReadMessageId || !loggedInArtistId) return;
    const requestKey = `${eventId}:${lastReadMessageId}:${loggedInArtistId}`;
    if (pendingReadReceiptKeysRef.current.has(requestKey)) return;
    if (completedReadReceiptKeysRef.current.has(requestKey)) return;
    pendingReadReceiptKeysRef.current.add(requestKey);
    try {
      await markEventChatMessagesRead({
        eventId,
        lastReadMessageId,
        userType: 'artist',
        userId: loggedInArtistId,
      });
      completedReadReceiptKeysRef.current.add(requestKey);
      if (completedReadReceiptKeysRef.current.size > 500) {
        const firstKey = completedReadReceiptKeysRef.current.values().next().value;
        if (firstKey) completedReadReceiptKeysRef.current.delete(firstKey);
      }
      refreshEventChatUnread();
    } catch {}
    finally {
      pendingReadReceiptKeysRef.current.delete(requestKey);
    }
  }, [loggedInArtistId, refreshEventChatUnread]);

  const markLatestRead = useCallback(() => {
    const currentEventId = selectedEventIdRef.current;
    const currentMessages = messagesRef.current;
    if (!currentEventId || currentMessages.length === 0) return;
    const newest = currentMessages[0]; // state は DESC(index0=最新)
    postEventReadReceipt(currentEventId, newest.id).catch(() => {});
    setEvents((prev) => prev.map((event) => (
      event.event_uuid === currentEventId ? { ...event, unreadCount: 0 } : event
    )));
    setUnreadMap((prev) => ({ ...prev, [currentEventId]: 0 }));
  }, [postEventReadReceipt]);

  const openBanner = useCallback((payload) => {
    if (payload?.id && lastBannerIdRef.current === payload.id) return;
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
      bannerTimeoutRef.current = null;
    }
    lastBannerIdRef.current = payload?.id ?? null;
    setBannerMsg(payload);
    setShowNewMsgBanner(true);
    bannerTimeoutRef.current = setTimeout(() => {
      setShowNewMsgBanner(false);
      lastBannerIdRef.current = null;
      bannerTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const j = await fetchJsonSafe(`${API_URL}/api/parts`, {}, { label: 'パーツ情報の取得に失敗しました。' });
        setPartsList(Array.isArray(j) ? j : (j?.parts || []));
      } catch (e) {
        setGlobalError(e.message);
        toastError(e.message || 'パーツ情報の取得に失敗しました。');
      }
    })();
  }, [setPartsList, toastError]);

  useEffect(() => {
    if (!selectedEventId) {
      setParticipantSnapshot(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchJsonSafe(
          ARTIST_PARTICIPANTS_URL(selectedEventId),
          {},
          { label: '参加者情報の取得に失敗しました。' }
        );
        if (!cancelled) setParticipantSnapshot(payload || null);
      } catch {
        if (!cancelled) setParticipantSnapshot(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId || !currentEventMembersVisibleToArtists) {
      setParticipantArtists([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const json = await listCommonEventCastingProgress(selectedEventId);
        if (!cancelled) {
          setParticipantArtists(Array.isArray(json) ? json : []);
        }
      } catch {
        if (!cancelled) setParticipantArtists([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId, currentEventMembersVisibleToArtists, setParticipantArtists]);

  useEffect(() => {
    if (!selectedEventId || !currentEventMembersVisibleToArtists) {
      setCastingInfo([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listEventCastingInfo(selectedEventId);
        if (!cancelled) setCastingInfo(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCastingInfo([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId, currentEventMembersVisibleToArtists, setCastingInfo]);

  useEffect(() => {
    try {
      if (!socket.connected) socket.connect();
      socket.emit('join_artists');
    } catch {}
  }, []);

  useEffect(() => {
    const onConnectError = () => {
      setGlobalError('サーバーに接続できません。時間を置いて再度お試しください。');
      toastError('サーバーに接続できません。時間を置いて再度お試しください。');
    };
    socket.on('connect_error', onConnectError);
    return () => socket.off('connect_error', onConnectError);
  }, [toastError]);

  const getEventDetail = useCallback(async (uuid) => {
    if (!uuid) return null;
    if (eventDetailCacheRef.current.has(uuid)) return eventDetailCacheRef.current.get(uuid);
    if (eventDetailInFlightRef.current.has(uuid)) return await eventDetailInFlightRef.current.get(uuid);

    const p = fetchJsonSafe(`${API_URL}/api/events/${uuid}`, {}, { label: 'イベント詳細の取得に失敗しました。' })
      .then(j => j.event)
      .finally(() => { eventDetailInFlightRef.current.delete(uuid); });

    eventDetailInFlightRef.current.set(uuid, p);
    const detail = await p;
    if (detail) eventDetailCacheRef.current.set(uuid, detail);
    return detail;
  }, []);

  const getEventComposite = useCallback(async (uuid) => {
    const d = await getEventDetail(uuid);
    if (!d) {
      return {
        event_uuid: uuid,
        flyer_front_url: undefined,
        flyer_back_url: undefined,
        event_date: undefined,
        open_time: undefined,
        start_time: undefined,
        venue: undefined,
        place: undefined,
        performance_type: undefined,
        name: undefined,
        venue_name: undefined,
        prefecture_name: undefined,
        prefecture: undefined,
        city: undefined,
        city_name: undefined,
        address: undefined,
        venue_address: undefined,
      };
    }
    let flyerFront = d.flyer_front_url;
    let flyerBack = d.flyer_back_url;
    if (d.performance_flag === 'additional' && d.use_existing_flyers && d.original_event_uuid) {
      const o = await getEventDetail(d.original_event_uuid);
      flyerFront = (o && o.flyer_front_url) ? o.flyer_front_url : flyerFront;
      flyerBack = (o && o.flyer_back_url) ? o.flyer_back_url : flyerBack;
    }
    const composite = {
      event_uuid: uuid,
      flyer_front_url: flyerFront,
      flyer_back_url: flyerBack,
      event_date: d.event_date,
      open_time: d.open_time,
      start_time: d.start_time,
      venue: d.venue,
      place: d.place,
      performance_type: d.performance_type,
      name: d.name,
      venue_name: d.venue_name,
      prefecture_name: d.prefecture_name,
      prefecture: d.prefecture,
      city: d.city,
      city_name: d.city_name,
      address: d.address,
      venue_address: d.venue_address,
    };
    composite.performance_type = normalizeEventPerformanceTypeForThread(
      composite,
      d.performance_type
    );
    return composite;
  }, [getEventDetail]);

  const getCompositesBatched = useCallback(async (uuids, concurrency = 4) => {
    const result = [];
    let i = 0;
    while (i < uuids.length) {
      const slice = uuids.slice(i, i + concurrency);
      const batch = await Promise.all(slice.map(id => getEventComposite(id)));
      result.push(...batch);
      i += concurrency;
    }
    return result;
  }, [getEventComposite]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId) {
      setSidebarDetails(null);
      setIsSidebarLoading(false);
      return undefined;
    }
    setIsSidebarLoading(true);
    setSidebarDetails(null);
    (async () => {
      try {
        const { event } = await fetchJsonSafe(ARTIST_SIDEBAR_ROW_URL(selectedEventId), {}, { label: 'サイドバーの取得に失敗しました。' });
        const comp = await getEventComposite(selectedEventId);
        const row = {
          ...event,
          flyer_front_url: event.flyer_front_url ?? comp.flyer_front_url,
          flyer_back_url: event.flyer_back_url ?? comp.flyer_back_url,
          event_date: event.event_date ?? comp.event_date,
          open_time: event.open_time ?? comp.open_time,
          start_time: event.start_time ?? comp.start_time,
          venue: event.venue ?? comp.venue,
          place: event.place ?? comp.place,
          name: event.name ?? comp.name,
          venue_name: event.venue_name ?? comp.venue_name,
          prefecture_name: event.prefecture_name ?? comp.prefecture_name,
          prefecture: event.prefecture ?? comp.prefecture,
          city: event.city ?? comp.city,
          city_name: event.city_name ?? comp.city_name,
          address: event.address ?? comp.address,
          venue_address: event.venue_address ?? comp.venue_address,
        };
        row.performance_type = normalizeEventPerformanceTypeForThread(
          row,
          event.performance_type ?? comp.performance_type
        );
        if (!cancelled) setSidebarDetails(row);
      } catch {
        if (!cancelled) setSidebarDetails(null);
      } finally {
        if (!cancelled) setIsSidebarLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId, getEventComposite]);

  const fetchSidebarRow = useCallback(async (eventId) => {
    const comp = await getEventComposite(eventId);
    try {
      const { event } = await fetchJsonSafe(ARTIST_SIDEBAR_ROW_URL(eventId), {}, { label: 'サイドバー情報の取得に失敗しました。' });
      const row = {
        ...event,
        flyer_front_url: event.flyer_front_url ?? comp.flyer_front_url,
        flyer_back_url: event.flyer_back_url ?? comp.flyer_back_url,
        event_date: event.event_date ?? comp.event_date,
        open_time: event.open_time ?? comp.open_time,
        start_time: event.start_time ?? comp.start_time,
        venue: event.venue ?? comp.venue,
        place: event.place ?? comp.place,
        name: event.name ?? comp.name,
        venue_name: event.venue_name ?? comp.venue_name,
        prefecture_name: event.prefecture_name ?? comp.prefecture_name,
        prefecture: event.prefecture ?? comp.prefecture,
        city: event.city ?? comp.city,
        city_name: event.city_name ?? comp.city_name,
        address: event.address ?? comp.address,
        venue_address: event.venue_address ?? comp.venue_address,
      };
      row.performance_type = normalizeEventPerformanceTypeForThread(
        row,
        event.performance_type ?? comp.performance_type
      );
      return row;
    } catch {
      return { ...(comp || {}), event_uuid: eventId };
    }
  }, [getEventComposite]);
  const fetchRepresentativeEventIdByThread = useCallback(async (threadId) => {
    const normalizedThreadId = String(threadId || '').trim();
    if (!normalizedThreadId) return null;
    try {
      const payload = await fetchJsonSafe(
        ARTIST_REPRESENTATIVE_EVENT_BY_THREAD_URL(normalizedThreadId),
        {},
        { label: 'スレッド情報の取得に失敗しました。' }
      );
      return payload?.event_uuid || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId || !isSelectedGroupUnified) {
      setGroupPanelEvents([]);
      return undefined;
    }
    (async () => {
      try {
        const payload = await fetchJsonSafe(ARTIST_GROUP_EVENTS_URL(selectedEventId), {}, { label: '' });
        if (cancelled) return;
        setGroupPanelEvents(
          normalizeGroupEventRows(payload?.events, { includeArtistVisibility: true })
        );
      } catch {
        if (!cancelled) setGroupPanelEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isSelectedGroupUnified, selectedEventId]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId || !isSelectedGroupUnified || effectiveGroupEvents.length <= 0) {
      setGroupEventSidebarRows([]);
      return undefined;
    }
    (async () => {
      const rows = await Promise.all(
        effectiveGroupEvents.map(async (base) => {
          const eventUuid = String(base?.event_uuid || '').trim();
          if (!eventUuid) return null;
          try {
            const detail = await fetchSidebarRow(eventUuid);
            return mergeEventSidebarRow({
              detail,
              base,
              eventUuid,
              pickFirstStringFn: pickFirstString,
            });
          } catch {
            return mergeEventSidebarRow({
              detail: null,
              base,
              eventUuid,
              pickFirstStringFn: pickFirstString,
            });
          }
        })
      );
      if (cancelled) return;
      setGroupEventSidebarRows(rows.filter(Boolean));
    })();
    return () => { cancelled = true; };
  }, [effectiveGroupEvents, fetchSidebarRow, isSelectedGroupUnified, selectedEventId]);

  const upsertEventRowAtTop = useCallback((row, { moveToTop = true } = {}) => {
    if (!row?.event_uuid) return;
    joinEventRooms({ socket, joinedRoomsRef, events: [row] });
    setEvents((prev) => {
      const previousRow =
        prev.find((eventRow) => eventRow && eventRow.event_uuid === row.event_uuid) || null;
      const patched = buildRealtimeEventSidebarPatch({
        row,
        previousRow,
        nowJstDbString,
        resolveEventThreadId,
        resolveEventChatMode,
        resolveEventGroupCount,
      });
      return upsertEventSidebarRowByUuid({
        prevEvents: prev,
        eventUuid: row.event_uuid,
        patch: patched,
        moveToTop,
        orderFrozen,
      });
    });
  }, [orderFrozen]);

  const fetchEvents = useCallback(async (mode = 'reset') => {
    const offsetForFetch = mode === 'append' ? eventsOffset : 0;
    try {
      setEventsError('');
      setIsLoadingEvents(true);
      if (mode !== 'append') {
        eventListForceFallbackExhaustedRef.current = false;
      }

      const { events: rawEvents = [] } = await loadEventListRows({
        mode,
        offset: offsetForFetch,
        headUrl: ARTIST_EVENTS_HEAD_URL,
        pageUrlBuilder: ARTIST_EVENTS_PAGE_URL,
        headModes: ['refreshTop'],
        fetchJsonSafe,
        errorLabel: 'イベント一覧の取得に失敗しました。',
      });
      const ids = rawEvents.map(evt => evt.event_uuid).filter(Boolean);

      let uMap = {};
      try {
        uMap = await fetchEventUnreadMap({
          apiUrl: API_URL,
          role: 'artist',
          eventIds: ids,
          fetchJsonSafe,
          label: '',
        });
        setUnreadMap(prev => ({ ...prev, ...uMap }));
      } catch {}

      const composites = await getCompositesBatched(ids, 4);
      const compMap = mapRowsByEventUuid(composites);
      const pageEvents = rawEvents.map((evt) =>
        composeArtistEventRowWithComposite({
          eventRow: evt,
          compositeRow: compMap.get(String(evt?.event_uuid || '').trim()),
          unreadCount: uMap[evt.event_uuid] || 0,
          preferEventPerformanceType: false,
        })
      );

      const modeState = resolveEventListModeState({
        mode,
        prevEvents: events,
        incomingEvents: pageEvents,
        selectedEventId,
        pageLimit: EVENTS_PAGE_LIMIT,
        refreshTopSliceToLimit: true,
        appendOffsetSource: 'incoming',
        appendHasMoreSource: 'incoming',
        appendHasMoreComparator: 'gte',
        appendJoinSource: 'incoming',
      });
      joinEventRooms({ socket, joinedRoomsRef, events: modeState.joinEvents });
      setEvents(modeState.nextEvents);
      if (modeState.offsetMode === 'set') {
        setEventsOffset(modeState.offsetValue);
      } else if (modeState.offsetMode === 'increment') {
        setEventsOffset((prev) => prev + modeState.offsetValue);
      }
      setHasMoreEvents(modeState.hasMoreEvents);

      return rawEvents.length;
    } catch (e) {
      setEventsError(e.message);
      toastError(e.message || 'イベント一覧の取得に失敗しました。');
      return 0;
    } finally {
      setIsLoadingEvents(false);
    }
  }, [events, eventsOffset, getCompositesBatched, selectedEventId, toastError]);

  const {
    searchSummaryLabel,
    openTargetSearchModal,
    clearSearch,
    searchToolbarChips,
    applyTargetSearchModal,
    requestRefreshTop,
    handleEventListScroll,
  } = useArtistEventChatListState({
    API_URL,
    ARTIST_SEARCH_API,
    EVENTS_PAGE_LIMIT,
    applyEventTargetSearchDraft,
    applySearchResultEvents,
    buildToolbarFilterChips,
    clearEventTargetSearch,
    composeArtistEventRowWithComposite,
    countActiveEventSearchFilters,
    displayedIdsRef,
    eventListForceFallbackExhaustedRef,
    eventListLoadMorePendingRef,
    eventListNode,
    eventUnreadMapArtist,
    events,
    fetchEvents,
    fetchJsonSafe,
    formatSearchChipDate,
    getCompositesBatched,
    hasMoreEvents,
    isBootLoading,
    isLoadingEvents,
    isSearchLoadingMore,
    isSearchMode,
    isSearching,
    joinEventRooms,
    joinedRoomsRef,
    loadEventSearchRowsWithUnread,
    loadMoreEventList,
    loadMoreNode,
    mapRowsByEventUuid,
    messageSearchQuery,
    observeEventListLoadMoreScroll,
    observeEventListLoadMoreSentinel,
    openEventTargetSearchModal,
    orderFrozen,
    prevUnreadMapRef,
    refreshTopTimerRef,
    resolveEventSearchCriteria,
    scheduleEventListRefreshTop,
    searchDraft,
    searchEndDate,
    searchHasMore,
    searchNextOffset,
    searchQuery,
    searchSnapshotToken,
    searchStartDate,
    setEvents,
    setEventsOffset,
    setHasMoreEvents,
    setIsSearchLoadingMore,
    setIsSearchMode,
    setIsSearching,
    setMessageSearchQuery,
    setSearchDraft,
    setSearchEndDate,
    setSearchError,
    setSearchHasMore,
    setSearchNextOffset,
    setSearchQuery,
    setSearchSnapshotToken,
    setSearchStartDate,
    setShowSearchModal,
    shouldLoadMoreEventList,
    socket,
    toastError,
    triggerEventListLoadMoreIfNeeded,
  });
  const {
    fetchMessages,
    loadNewerMessages,
    fetchNewMessages,
    reconcileRecentMessages,
    buildRealtimeEventTextMessage,
  } = useArtistEventChatMessages({
    messagesRef,
    ACCESS_DENIED_STATUSES,
    ADMIN_MESSAGE_FORMATS,
    API_URL,
    EARLIEST_CREATED_AT,
    RECON_LIMIT,
    anchorHighlightTimerRef,
    chatContainerRef,
    fetchJsonSafe,
    fetchMessagesRef,
    fetchNewMessagesRef,
    focusChatAnchorMessage,
    hasNewerMessages,
    initialLoadRef,
    isFetching,
    lastDiffFetchRef,
    latestCreatedAtRef,
    limit,
    loadingNewerMessages,
    loggedInArtistId,
    mergeMessages,
    messageIdsRef,
    messageWindowOffset,
    messages,
    patchMessages,
    pickFirstString,
    pickRealtimeReadCount,
    postEventReadReceipt,
    selectedEventId,
    selectedEventIdRef,
    setActiveAnchorMessageId,
    setEvents,
    setFatalAccessError,
    setHasMore,
    setHasNewerMessages,
    setInlineCopyMessageId,
    setIsFetching,
    setIsInitialLoading,
    setLatestCreatedAt,
    setLoadedMessagesEventId,
    setLoadingNewerMessages,
    setLoadingOlderMessages,
    setMessageWindowOffset,
    setMessages,
    setMessagesError,
    setOffset,
    setSelectedEventInfo,
    setUnreadMap,
    toastError,
  });

  const fetchEventDetail = useCallback(async (eventId) => {
    try {
      setDetailError('');
      const d = await getEventDetail(eventId);
      if (!d) return;
      const listRow = events.find((evt) => evt?.event_uuid === eventId) || null;
      const sidebarRow = sidebarDetails?.event_uuid === eventId ? sidebarDetails : null;
      let merged = { ...d };
      if (d.performance_flag === 'additional' && d.use_existing_flyers && d.original_event_uuid) {
        const o = await getEventDetail(d.original_event_uuid);
        if (o) {
          merged.flyer_front_url = o.flyer_front_url || merged.flyer_front_url;
          merged.flyer_back_url = o.flyer_back_url || merged.flyer_back_url;
        }
      }
      merged = {
        ...(listRow || {}),
        ...(sidebarRow || {}),
        ...merged,
        chat_thread_id:
          resolveEventThreadId(merged) ||
          resolveEventThreadId(sidebarRow) ||
          resolveEventThreadId(listRow) ||
          null,
        chat_mode:
          resolveExplicitEventChatMode(merged || {}) ??
          resolveExplicitEventChatMode(sidebarRow || {}) ??
          resolveExplicitEventChatMode(listRow || {}) ??
          'separate',
        group_event_count:
          resolveExplicitEventGroupCount(merged || {}) ??
          resolveExplicitEventGroupCount(sidebarRow || {}) ??
          resolveExplicitEventGroupCount(listRow || {}) ??
          1,
      };
      setSelectedEventInfo(merged);
    } catch (e) {
      setDetailError(e.message);
      if (ACCESS_DENIED_STATUSES.has(e.status)) {
        setFatalAccessError(true);
        setSelectedEventInfo(null);
        setMessages([]);
        toastError('このイベントにアクセスする権限がありません。');
      } else {
        toastError(e.message || 'イベント詳細の取得に失敗しました。');
      }
    }
  }, [events, getEventDetail, sidebarDetails, toastError]);

  useEffect(() => {
    if (!selectedEventInfo?.event_date) { setIsSendLocked(false); return; }
    const end = endOfDayJst(selectedEventInfo.event_date);
    if (!end || isNaN(end.getTime())) { setIsSendLocked(false); return; }
    const lockAt = new Date(end.getTime() + 30 * MS_DAY);
    setIsSendLocked(new Date() >= lockAt);
  }, [selectedEventInfo, setIsSendLocked]);

  const sendBlockedMessage = isSendLocked
    ? 'イベント終了から1ヵ月経過のため、送信できません。'
    : '現在メンテナンス中のため送信できません。';

  const bumpEventToTop = useCallback((list, eventId, patch = {}) => {
    const idx = list.findIndex(e => e.event_uuid === eventId);
    if (idx === -1) return list;
    const updated = { ...list[idx], ...patch };
    const others = list.slice(0, idx).concat(list.slice(idx + 1));
    return [updated, ...others];
  }, []);
  const updateSidebarAfterSend = useCallback((previewBase) => {
    const preview = previewBase.length > 50 ? `${previewBase.slice(0, 50)}…` : previewBase;
    const nowDb = nowJstDbString();
    if (isSearchMode) {
      setEvents(prev =>
        prev.map(e =>
          e.event_uuid === selectedEventId
            ? { ...e, last_message_preview: preview, last_message_time: nowDb, unreadCount: 0 }
            : e
        )
      );
    } else {
      setEvents(prev =>
        bumpEventToTop(prev, selectedEventId, {
          last_message_preview: preview,
          last_message_time: nowDb,
          unreadCount: 0,
        })
      );
    }
  }, [bumpEventToTop, isSearchMode, selectedEventId]);
  const handleBannerClick = async () => {
    setShowNewMsgBanner(false);
    try {
      if (selectedEventIdRef.current) {
        setActiveAnchorMessageId('');
        setHasNewerMessages(false);
        await fetchMessages(selectedEventIdRef.current, true, 0, { anchorMessageId: '' });
      }
      const c = chatContainerRef.current;
      if (c) c.scrollTop = c.scrollHeight;
      markLatestRead();
    } catch (error) {
      dbg('handleBannerClick failed', error);
    }
  };

  const BASE_LAST_KEY = 'artist-eventchat:last';
  const userLastKey = useMemo(
    () => (loggedInArtistId ? `${BASE_LAST_KEY}:${loggedInArtistId}` : null),
    [loggedInArtistId]
  );
  const loadLast = useCallback(() => {
    try {
      const sUser = userLastKey ? sessionStorage.getItem(userLastKey) : null;
      if (sUser) return JSON.parse(sUser);
      const sBase = sessionStorage.getItem(BASE_LAST_KEY);
      if (sBase) return JSON.parse(sBase);
    } catch (e) { dbg('loadLast parse error', e); }
    return null;
  }, [userLastKey]);
  const saveLast = useCallback((eventId, view = 'chat') => {
    const obj = { eventId, view, ts: Date.now() };
    try {
      if (userLastKey) sessionStorage.setItem(userLastKey, JSON.stringify(obj));
      sessionStorage.setItem(BASE_LAST_KEY, JSON.stringify(obj));
    } catch (e) { dbg('saveLast error', e); }
  }, [userLastKey]);
  const clearLast = useCallback(() => {
    try {
      if (userLastKey) sessionStorage.removeItem(userLastKey);
      sessionStorage.removeItem(BASE_LAST_KEY);
    } catch (e) { dbg('clearLast error', e); }
  }, [userLastKey]);

  useEffect(() => {
    if (!isMobile || mobileView !== 'list') return;
    if (!mobileEventListPrefRef.current) return;

    pendingRestoreRef.current = false;
    clearLast();

    if (currentRoomRef.current) {
      try { socket.emit('leave_event', currentRoomRef.current); } catch {}
      currentRoomRef.current = null;
    }

    setSelectedEventId(null);
    setSelectedEventInfo(null);
    setMessages([]);
    messageIdsRef.current = new Set();
    setOffset(0);
    setHasMore(true);
    setMessageWindowOffset(0);
    setHasNewerMessages(false);
    setActiveAnchorMessageId('');
    setLatestCreatedAt(null);
    setShowNotes(false);
    setMessagesError('');
    setDetailError('');
    notesModeRef.current = 'list';
    setFatalAccessError(false);
    setShowNewMsgBanner(false);
    if (bannerTimeoutRef.current) { clearTimeout(bannerTimeoutRef.current); bannerTimeoutRef.current = null; }
    lastBannerIdRef.current = null;
  }, [isMobile, mobileView, clearLast, mobileEventListPrefRef, pendingRestoreRef]);

  const suppressRestoreRef = useRef(false);
  const initialAppliedRef = useRef(false);

  // ===== イベント選択（スマホではチャットをフル画面にし、pushStateで戻れるように） =====
  const handleSelectEvent = useCallback((uuid, opts = {}) => {
    const normalizedAnchorMessageId = String(opts?.anchorMessageId || '').trim();
    dbg('handleSelectEvent', { uuid });
    setFatalAccessError(false);

    if (uuid === selectedEventId && !normalizedAnchorMessageId) {
      if (isMobile && mobileView !== 'chat') {
        mobileEventListPrefRef.current = false;
        if (!mobileEventChatHistoryRef.current) {
          try {
            window.history.pushState({ aecp: 'chat', eventId: uuid }, '');
            mobileEventChatHistoryRef.current = true;
          } catch {
            mobileEventChatHistoryRef.current = false;
          }
        }
        setMobileView('chat');
      }
      return;
    }

    mobileEventListPrefRef.current = false;
    mobileEventChatHistoryRef.current = false;

    if (currentRoomRef.current) socket.emit('leave_event', currentRoomRef.current);
    socket.emit('join_event', uuid);
    currentRoomRef.current = uuid;

    setUnreadMap(prev => ({ ...prev, [uuid]: 0 }));
    setEvents(prev => prev.map(e => (e.event_uuid === uuid ? { ...e, unreadCount: 0 } : e)));

    setSelectedEventId(uuid);
    setSelectedEventInfo(null);
    setMessages([]);
    messageIdsRef.current = new Set();
    setOffset(0);
    setHasMore(true);
    setMessageWindowOffset(0);
    setHasNewerMessages(false);
    setActiveAnchorMessageId('');
    setLatestCreatedAt(null);
    setShowNotes(false);
    setMessagesError('');
    setDetailError('');
    notesModeRef.current = 'list';

    saveLast(uuid, 'chat');

    // スマホ：チャットビューへ遷移（ブラウザバックで戻れる）
    if (isMobile) {
      if (!mobileEventChatHistoryRef.current) {
        try {
          window.history.pushState({ aecp: 'chat', eventId: uuid }, '');
          mobileEventChatHistoryRef.current = true;
        } catch {
          mobileEventChatHistoryRef.current = false;
        }
      }
      setMobileView('chat');
    }

    fetchEventDetail(uuid);
    fetchMessages(uuid, true, 0, { anchorMessageId: normalizedAnchorMessageId })
      .then(() => requestRefreshTop())
      .catch(() => {});
    refreshEventChatUnread();
  }, [fetchEventDetail, fetchMessages, isMobile, mobileView, refreshEventChatUnread, requestRefreshTop, saveLast, selectedEventId, mobileEventChatHistoryRef, mobileEventListPrefRef, setMobileView]);

  // ===== 初期化 =====
  const __params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialThreadId = __params.get('thread');
  const initialEventId = __params.get('event');
  const initialMessageId = readChatQuestionMessageId(__params);
  const initialView = (__params.get('view') || 'chat');

  useEffect(() => {
    if (initialAppliedRef.current) return;
    initialAppliedRef.current = true;

    (async () => {
      setIsBootLoading(true);
      try {
        await fetchEvents('reset');
      } finally {}
      const fromSidebarReset = Boolean(location?.state?.reset);

      if (fromSidebarReset) {
        suppressRestoreRef.current = true;
        clearLast();
        pendingRestoreRef.current = false;
        mobileEventListPrefRef.current = true;
        mobileEventChatHistoryRef.current = false;
        if (isMobile) setMobileView('list');
        navigate('/artist/workspaces/event-operations/event-chat', { replace: true, state: null });
      } else {
        const hasInitialTarget = Boolean(initialThreadId || initialEventId);
        let initialTargetEventId = null;
        if (initialThreadId) {
          initialTargetEventId = await fetchRepresentativeEventIdByThread(initialThreadId);
          dbg('boot thread param resolve', { initialThreadId, initialTargetEventId });
        }
        if (!initialTargetEventId && initialEventId) {
          initialTargetEventId = initialEventId;
        }

        if (initialTargetEventId) {
          handleSelectEvent(initialTargetEventId, { anchorMessageId: initialMessageId });
          if (initialView === 'notes') {
            setShowNotes(true);
            saveLast(initialTargetEventId, 'notes-list');
          } else {
            saveLast(initialTargetEventId, 'chat');
          }
          navigate('/artist/workspaces/event-operations/event-chat', { replace: true, state: null });
        } else if (hasInitialTarget) {
          pendingRestoreRef.current = false;
          mobileEventListPrefRef.current = true;
          mobileEventChatHistoryRef.current = false;
          if (isMobile) setMobileView('list');
          navigate('/artist/workspaces/event-operations/event-chat', { replace: true, state: null });
        } else if (!suppressRestoreRef.current) {
          const last = loadLast();
          if (last?.eventId) {
            handleSelectEvent(last.eventId);
            if (last.view === 'notes-list') setShowNotes(true);
          } else {
            // スマホ初期は一覧を全面
            if (isMobile) setMobileView('list');
            pendingRestoreRef.current = false;
          }
        }
      }
    })().finally(() => {
      setIsBootLoading(false);
      requestAnimationFrame(() => setOrderFrozen(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialThreadId, initialEventId, initialMessageId, initialView, location.state]);

  // サイドバー/フッターから「未選択で開く」指示を受けたとき
  useEffect(() => {
    if (!location?.state?.reset) return;

    clearLast();
    pendingRestoreRef.current = false;

    if (currentRoomRef.current) {
      try { socket.emit('leave_event', currentRoomRef.current); } catch {}
      currentRoomRef.current = null;
    }

    setSelectedEventId(null);
    setSelectedEventInfo(null);
    setMessages([]);
    messageIdsRef.current = new Set();
    setOffset(0);
    setHasMore(true);
    setMessageWindowOffset(0);
    setHasNewerMessages(false);
    setActiveAnchorMessageId('');
    setLatestCreatedAt(null);
    setShowNotes(false);
    setMessagesError('');
    setDetailError('');
    notesModeRef.current = 'list';
    setFatalAccessError(false);
    setShowNewMsgBanner(false);
    if (bannerTimeoutRef.current) { clearTimeout(bannerTimeoutRef.current); bannerTimeoutRef.current = null; }
    lastBannerIdRef.current = null;
    mobileEventListPrefRef.current = true;
    mobileEventChatHistoryRef.current = false;

    fetchEvents('reset');
    navigate('/artist/workspaces/event-operations/event-chat', { replace: true, state: null });

    if (isMobile) setMobileView('list');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, isMobile, clearLast, navigate]);

  // ===== ブラウザバック（スマホ：チャット→一覧） =====
  useEffect(() => {
    const onPop = (event) => {
      if (shouldIgnorePopStateForHistoryDismiss(event)) return;
      // ノート詳細優先の戻る処理（既存）
      if (window.__artistNotesImageOpen) return;
      if (window.__artistNotesImagePoppedAt && Date.now() - window.__artistNotesImagePoppedAt < 400) return;

      if (imageModalHistoryRef.current.closing) {
        imageModalHistoryRef.current.closing = false;
        imageModalHistoryRef.current.pushed = false;
        return;
      }
      if (imgModalOpen) {
        imageModalHistoryRef.current.pushed = false;
        closeImageModal(true);
        return;
      }

      if (isMobile && mobileView === 'chat') {
        mobileEventListPrefRef.current = true;
        mobileEventChatHistoryRef.current = false;
        setMobileView('list');
        return;
      }
      if (!selectedEventId) return;
      if (showNotes) {
        if (notesModeRef.current === 'detail') {
          window.dispatchEvent(new CustomEvent('artist-notes-close-detail'));
        } else {
          setShowNotes(false);
          if (selectedEventId) saveLast(selectedEventId, 'chat');
        }
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isMobile, mobileView, showNotes, selectedEventId, saveLast, imgModalOpen, closeImageModal, mobileEventChatHistoryRef, mobileEventListPrefRef, setMobileView]);

  const {
    shouldShowMessageSourceBadge,
    withAnnouncementScopeTitle,
    renderMessageContent,
    getMainPartLabel,
    handleAvatarClick,
    renderAnnouncementAvatarLeft,
    renderAdminAvatarLeft,
    renderArtistAvatarLeft,
    formatEventListTimestamp,
    toggleNotes,
    dropAreaRef,
    allowedDnd,
    isDndDragging,
    overlayRect,
    busyActive,
    composerRef,
  } = useArtistEventChatRuntime({
    ADMIN_MESSAGE_FORMATS,
    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_PROFILE_IMAGE_PATH,
    CHAT_MODE_GROUP_UNIFIED,
    EventNoteActionButton,
    MAX_FILE_BYTES,
    MAX_IMAGE_COUNT,
    MESSAGE_FILE_PATH,
    SOCKET_EVENTS,
    STALE_MS,
    adminLookup,
    appendVariant,
    bannerTimeoutRef,
    buildAssetUrl,
    buildEventAnnouncementChecks,
    buildMessageImageUrl,
    buildParticipantEventMembershipLabel,
    buildParticipantEventPartSummary,
    buildRealtimeEventTextMessage,
    bumpEventToTop,
    castingInfoMap,
    chatContainerRef,
    cleanupTimeoutRefs,
    createEventAvatarProfileTargetResolver,
    createEventAvatarRenderers,
    createEventMessageRenderPipeline,
    createEventPipelineOptions,
    createEventProfilePayloadBuilders,
    effectiveGroupEvents,
    ellipsizeFileName,
    fatalAccessError,
    fetchMessages,
    fetchMessagesRef,
    fetchNewMessages,
    fetchNewMessagesRef,
    fetchSidebarRow,
    formatAdminDisplayName,
    formatExpireDateLabel,
    getAnnouncementIconClass,
    getEventComposite,
    getFileIconClass,
    getMainPartLabelRef,
    handleDownload,
    handleMentionClick,
    hasMore,
    hasNewerMessages,
    isAnnouncementMessage,
    isEventFinishedMessage,
    isEventImagesAddedMessage,
    isEventInfoPublishedMessage,
    isEventInfoUpdatedMessage,
    isEventMaterialSharedMessage,
    isEventMembersPublishedMessage,
    isFetching,
    isNearTopForPaging,
    isNoteCreateMessage,
    isNoteDeleteMessage,
    isNoteMessage,
    isNoteUpdateMessage,
    isReadOnly,
    isSearchModeRef,
    isSelectedGroupUnified,
    isSendLocked,
    isSimpleAnnouncementMessage,
    isStageSeatingSharedMessage,
    isTimetableCreatedMessage,
    isTimetableMemoMessage,
    isTimetableRuntimeMessage,
    isTimetableUpdatedMessage,
    isUploading,
    isViewingLatest,
    lastDiffFetchRef,
    linkifyText,
    loadNewerMessages,
    loadingNewerMessages,
    markLatestRead,
    mergeMessages,
    messageIdsRef,
    messages,
    messagesRef,
    normalizeEventUuidList,
    normalizePartLabel,
    normalizeServerMessage,
    notesModeRef,
    nowJstDbString,
    offset,
    openBanner,
    openImageModal,
    openProfileTarget,
    parseEventTimetableRuntimePayload,
    parsePartIds,
    participantArtistMap,
    participantSnapshot,
    partsLookup,
    patchMessageReadCountsByIds,
    pickFirstString,
    postEventReadReceipt,
    reconcileRecentMessages,
    reconcileTimerRef,
    refreshAnnouncements,
    refreshEventChatUnread,
    refreshMessageReactionDetails,
    refreshPendingMentionTargets,
    refreshTopTimerRef,
    removeMessages,
    replyDraft,
    requestRefreshTop,
    resolveEventChatMode,
    resolveEventGroupCount,
    resolveEventTimetableRuntimeVisibility,
    resolveThreadIdByEvent,
    saveLast,
    selectedEventId,
    selectedEventIdRef,
    selectedEventInfo,
    selectedThreadIdRef,
    sendEventChatFileMessage,
    sendEventChatImageMessages,
    setEvents,
    setIsUploading,
    setMessages,
    setOffset,
    setReplyDraft,
    setShowAlbumModal,
    setShowMaterialsModal,
    setShowNewMsgBanner,
    setShowNotes,
    setSidebarDetails,
    setUnreadMap,
    shouldMarkLatestAsRead,
    shouldShowEventMessageSourceBadge,
    showNotes,
    showNotesRef,
    sidebarTimeLabelJST,
    socket,
    statusToDisplayMessage,
    toNum,
    toastError,
    updateSidebarAfterSend,
    upsertEventRowAtTop,
    useChatDragDrop,
  });

  // ====== UI ======
  const containerMobileClass =
    isMobile ? (mobileView === 'chat' ? ' is-mobile is-mobile-chat' : ' is-mobile is-mobile-list') : '';

  return (
    <div className="artist-dashboard">
      <ArtistSidebar />
      <div className={`artist-event-message-container${containerMobileClass}`}>
        <ChatWorkspaceLayout className="artist-event-chat-workspace">

        {/* サイドバー（モバイルでは is-mobile-list のときだけ表示） */}
        <div className="event-sidebar chat-target-sidebar">
          <ChatTargetSidebarToolbar
            onOpenSearch={openTargetSearchModal}
            summary={searchSummaryLabel}
            chips={searchToolbarChips}
          />

          <ul
            className="event-list"
            ref={setEventListNode}
            onScroll={handleEventListScroll}
            aria-busy={isBootLoading ? 'true' : 'false'}
          >
            {isBootLoading ? (
              <ChatTargetSidebarLoading
                variant="event"
                count={4}
                showLoadMore={!isSearchMode}
                loadMoreClassName="event-list__loadmore"
                loadMoreButtonClassName="event-list__loadmore-btn"
              />
            ) : (
              <>
                {isSearchMode ? (
                  <li className="event-list__result-header">
                    <span className="event-list__result-count">
                      <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                      検索結果 {formatItemCount(events.length || 0)}{searchHasMore ? '+' : ''}
                    </span>
                    <button type="button" className="event-list__result-clear" onClick={clearSearch}>
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                      <span>解除</span>
                    </button>
                  </li>
                ) : null}
                {events.length === 0 && (
                  <li className="event-list__empty-hint">
                    {isSearchMode
                      ? '一致するチャットはありません。条件を変えるか「解除」してください。'
                      : '該当するチャットがありません。'}
                  </li>
                )}
                {events.map((evt) => {
                  if (!evt) return null;
                  const rowKey = evt.search_hit_message_id
                    ? `${evt.event_uuid}-${evt.search_hit_message_id}`
                    : String(evt.event_uuid || '');
                  const previewText = resolveSidebarPreview(evt);
                  const isGroupUnifiedRow = isUnifiedEventGroupChatRow(evt);
                  const isMessageHit = evt.search_result_type === 'message-hit';
                  const unreadCount = evt.event_uuid === selectedEventId ? 0 : Number(evt.unreadCount || 0);
                  // 検索中は受信箱の未対応メンションCTAを結果に混ぜない
                  const mentionTask = isSearchMode
                    ? null
                    : pendingMentionTargets[String(evt.event_uuid || '').trim()] || null;
                  return (
                    <ChatTargetListRow
                      key={rowKey}
                      classNames={EVENT_TARGET_ROW_CLASSES}
                      className={`${evt.event_uuid === selectedEventId ? 'is-active' : ''}${
                        mentionTask?.count > 0 ? ' event-target-row--has-mention' : ''
                      }`}
                      onClick={() =>
                        handleSelectEvent(evt.event_uuid, {
                          anchorMessageId: String(evt?.search_hit_message_id || '').trim(),
                        })
                      }
                      ariaCurrent={evt.event_uuid === selectedEventId ? 'true' : undefined}
                      avatar={
                        <EventFlyerMedia
                          event={evt}
                          imageUrl={evt.flyer_front_url ? buildFlyerUrl(evt.flyer_front_url, 'card') : ''}
                          imageAlt="フライヤー"
                          sizeVariant="small"
                          borderRadius={10}
                          placeholderClassName="event-list__flyer-placeholder"
                          imageClassName="event-list__flyer"
                          hasImage={Boolean(evt.flyer_front_url)}
                          onImageError={(target) => { target.style.display = 'none'; }}
                        />
                      }
                      title={evt.name}
                      previewNode={
                        isMessageHit ? (
                          <span className="event-list__last-preview">
                            {evt.search_hit_sender_name ? (
                              <span className="event-list__hit-sender">{evt.search_hit_sender_name}：</span>
                            ) : null}
                            {renderChatSearchHighlight(previewText, messageSearchQuery)}
                          </span>
                        ) : (
                          <>
                            {!isGroupUnifiedRow && evt.performance_type ? (
                            <span className="event-list__performance">{evt.performance_type}</span>
                            ) : null}
                            {previewText ? (
                              <span className="event-list__last-preview">{previewText}</span>
                            ) : null}
                          </>
                        )
                      }
                      belowContent={
                        mentionTask?.count > 0 ? (
                          <ChatMentionTaskNotice
                            count={mentionTask.count}
                            disabled={
                              resolvingMentionTargetId === String(evt.event_uuid || '').trim() ||
                              loadingMentionResolutionTargetKey === `event:${String(evt.event_uuid || '').trim()}`
                            }
                            onResolve={() =>
                              openMentionResolution({
                                scope: 'event',
                                targetId: evt.event_uuid,
                                count: mentionTask.count,
                                title: evt.name ? `${evt.name} の未対応メンション` : '未対応メンション',
                                resolveTarget: resolveMentionTarget,
                              })
                            }
                            onOpen={() =>
                              handleSelectEvent(evt.event_uuid, {
                                anchorMessageId: String(mentionTask.messageId || '').trim(),
                              })
                            }
                          />
                        ) : null
                      }
                      time={formatEventListTimestamp(evt.last_message_time)}
                      badge={!isMessageHit && unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : null}
                    />
                  );
                })}

                {!isSearchMode && (
                  <li
                    className="event-list__loadmore"
                    ref={setLoadMoreNode}
                    aria-busy={isLoadingEvents ? 'true' : 'false'}
                  >
                    {isLoadingEvents ? (
                      <>
                        <div className="chat-spinner chat-spinner--small" aria-hidden="true"></div>
                        <span className="sr-only">読み込み中…</span>
                      </>
                    ) : hasMoreEvents ? 'スクロールで自動取得' : ''}
                  </li>
                )}
                {isSearchMode && events.length > 0 && (searchHasMore || isSearchLoadingMore) ? (
                  <li
                    className="event-list__loadmore"
                    aria-busy={isSearchLoadingMore ? 'true' : 'false'}
                  >
                    {isSearchLoadingMore ? (
                      <span role="status" aria-live="polite">過去の検索結果を読み込み中…</span>
                    ) : (
                      'スクロールで過去の結果を追加'
                    )}
                  </li>
                ) : null}
              </>
            )}
          </ul>
        </div>

        <ArtistEventChatSearchModal
          isOpen={showSearchModal}
          onClose={() => {
            setShowSearchModal(false);
            setSearchError('');
          }}
          onApply={applyTargetSearchModal}
          onClear={clearSearch}
          summary={searchSummaryLabel}
          applying={isSearching}
          errorMessage={searchError}
          onRetry={applyTargetSearchModal}
          retrying={isSearching}
          searchDraft={searchDraft}
          setSearchDraft={setSearchDraft}
        />

        {/* チャットエリア（モバイルでは is-mobile-chat のときだけ表示 / D&D対応） */}
        <div className={`chat-main-with-side${isSidePanelOpen ? '' : ' is-side-panel-collapsed'}`}>
          <div
            className={`event-chat-area chat-area chat-dnd-scope${(isDndDragging && allowedDnd) ? ' is-dragging' : ''}`}
            ref={dropAreaRef}
            aria-busy={busyActive ? 'true' : 'false'}
          >
          <ChatDragOverlay
            show={allowedDnd && isDndDragging}
            rect={overlayRect}
            title="ここにドロップで送信"
            sub="画像（最大5枚）またはファイル1点（100MBまで）"
          />

          {(showDelayedInitialLoading || isUploading) && (
            <div className="chat-loading-overlay" aria-label={isUploading ? '送信中' : '読み込み中'}>
              <div className="chat-spinner" aria-hidden="true"></div>
            </div>
          )}

          {!selectedEventId ? (
            <ChatEmptyState ariaLabel="イベントチャットが未選択です" />
          ) : fatalAccessError ? (
            <div className="event-chat-placeholder" role="alert">
              <i className="fa-solid fa-lock" aria-hidden="true"></i>
              <p>このイベントにはアクセスできません。</p>
            </div>
          ) : (
            <>
              {selectedEventInfo && (
                <ChatAreaHeader
                  className="event-chat-header"
                  disableMainWrapper
                  main={(
                    <>
                      <div className="event-header-left">
                        {isMobile && mobileView === 'chat' && (
                          <ChatHeaderIconButton
                            className={mobileBackButtonClassName}
                            onClick={() => {
                              mobileEventListPrefRef.current = true;
                              mobileEventChatHistoryRef.current = false;
                              if (window.history.state && window.history.state.aecp === 'chat') {
                                try { window.history.back(); } catch { setMobileView('list'); }
                              } else {
                                setMobileView('list');
                              }
                            }}
                            aria-label="イベント一覧に戻る"
                            title="イベント一覧に戻る"
                            iconClassName="fa-solid fa-chevron-left"
                          />
                        )}

                        <button
                          type="button"
                          className={`flyer-frame${selectedEventFlyerUrl ? '' : ' is-placeholder'}`}
                          onClick={handleGoHeaderEventDetail}
                          aria-label="イベント詳細を開く"
                          title="イベント詳細を開く"
                        >
                          {selectedEventFlyerUrl ? (
                            <img
                              src={selectedEventFlyerUrl}
                              alt={`${selectedEventInfo.name || 'イベント'}のフライヤー`}
                              className="flyer-image"
                              onLoad={(e) => applyLandscapeContain(e.currentTarget)}
                            />
                          ) : (
                            <EventFlyerMedia
                              event={selectedEventInfo}
                              imageUrl=""
                              sizeVariant="small"
                              borderRadius={8}
                              hasImage={Boolean(selectedEventFlyerUrl || selectedEventFlyerBackUrl)}
                            />
                          )}
                        </button>
                      </div>

                      <div className="event-header-main">
                        <div className="event-meta-top">
                          <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
                          <span className="event-date__value">{eventHeaderSchedule.date || '未設定'}</span>
                          {eventHeaderSchedule.time && (
                            <span className="event-date__time">{eventHeaderSchedule.time}</span>
                          )}
                        </div>

                        <div className="event-title-stack">
                          <button
                            type="button"
                            className="event-title-link"
                            onClick={handleGoHeaderEventDetail}
                            title={isSelectedGroupUnified ? '次の公演のイベント詳細を開く' : 'イベント詳細を開く'}
                          >
                            {selectedEventInfo.name || 'イベント名未設定'}
                          </button>
                          {isSelectedGroupUnified && headerNextPerformance?.label ? (
                            <div className="event-next-line">
                              {headerNextPerformance.label}
                            </div>
                          ) : null}
                          {isSelectedMultiPerformanceEvent && headerNextMultiPerformance?.label ? (
                            <div className="event-next-line">
                              {headerNextMultiPerformance.label}
                            </div>
                          ) : null}
                          {!isSelectedGroupUnified && !isSelectedMultiPerformanceEvent && headerPerformanceType && (
                            <div className="event-performance-line">
                              {headerPerformanceType}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  actionsClassName="event-header-actions chat-header-action-group"
                  actions={(
                    <>
                      <ChatHeaderIconButton
                        className="member-icon"
                        disabled={!selectedEventId}
                        onClick={toggleNotes}
                        title={showNotes ? 'チャットに戻る' : 'ノート'}
                        aria-label={showNotes ? 'チャットに戻る' : 'ノート'}
                        iconClassName={`fa-solid ${showNotes ? 'fa-comment' : 'fa-note-sticky'}`}
                        badgeCount={showNotes ? 0 : noteCount}
                      />
                        <ChatHeaderIconButton
                          className="member-icon"
                          disabled={!selectedEventId}
                          onClick={() => setIsMemberModalOpen(true)}
                          title="メンバーリスト"
                          aria-label="メンバーリスト"
                          iconClassName="fa-solid fa-people-group"
                          badgeCount={membersVisibleToArtists ? participantCounts.total : null}
                          showZeroBadge={membersVisibleToArtists}
                        />
                      <div className="event-header-menu-wrap">
                        <button
                          type="button"
                          className={`member-icon${assetMenuBadgeTone && assetMenuBadgeTone !== 'none' ? ' has-event-assets' : ''}`}
                          disabled={!selectedEventId}
                          onClick={toggleChatHeaderMenu}
                          title="メニュー"
                          aria-label="メニュー"
                        >
                          <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>
                          {assetMenuBadgeTone && assetMenuBadgeTone !== 'none' ? (
                            <span className={`event-header-menu-dot is-${assetMenuBadgeTone}`} aria-hidden="true" />
                          ) : null}
                        </button>
                        {chatHeaderMenuOpen && chatHeaderMenuPosition && typeof document !== 'undefined' ? createPortal(
                          <div
                            className="event-header-menu event-header-menu--floating"
                            role="menu"
                            style={{
                              left: `${chatHeaderMenuPosition.left}px`,
                              top: `${chatHeaderMenuPosition.top}px`,
                            }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                openMaterialsModal();
                                closeChatHeaderMenu();
                              }}
                            >
                              <i className="fa-solid fa-folder-open" aria-hidden="true"></i>
                              <span>イベント資料</span>
                              {materialBadge.count > 0 ? (
                                <span className="event-header-menu-badge">
                                  <span className={`event-header-menu-dot is-${materialBadge.tone}`} aria-hidden="true" />
                                  <span>{materialBadge.count}</span>
                                </span>
                              ) : null}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                openAlbumModal();
                                closeChatHeaderMenu();
                              }}
                            >
                              <i className="fa-solid fa-images" aria-hidden="true"></i>
                              <span>イベントアルバム</span>
                              {albumBadge.count > 0 ? (
                                <span className="event-header-menu-badge">
                                  <span className={`event-header-menu-dot is-${albumBadge.tone}`} aria-hidden="true" />
                                  <span>{albumBadge.count}</span>
                                </span>
                              ) : null}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                openLinksModal();
                                closeChatHeaderMenu();
                              }}
                            >
                              <i className="fa-solid fa-link" aria-hidden="true"></i>
                              <span>リンク</span>
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                openMediaModal();
                                closeChatHeaderMenu();
                              }}
                            >
                              <i className="fa-solid fa-photo-film" aria-hidden="true"></i>
                              <span>写真・ファイル</span>
                            </button>
                          </div>
                        , document.body) : null}
                      </div>
                      </>
                    )}
                  />
              )}
              <ChatQuestionPendingBar />
              {selectedEventId && !showNotes ? (
                <ChatAnnouncementBar
                  announcement={latestAnnouncement}
                  count={announcementCount}
                  onOpenList={() => setIsAnnouncementListOpen(true)}
                />
              ) : null}

              {!showNotes && showNewMsgBanner && bannerMsg && (
                <div className="event-chat-new-msg-banner" onClick={handleBannerClick}>
                  {bannerMsg.avatarUrl && failedBannerAvatarUrl !== bannerMsg.avatarUrl && (
                    <img
                      src={bannerMsg.avatarUrl}
                      alt="avatar"
                      className="event-chat-banner-avatar"
                      onError={() => setFailedBannerAvatarUrl(bannerMsg.avatarUrl)}
                    />
                  )}
                  <div className="event-chat-banner-text">
                    <div className="event-chat-banner-name">
                      {bannerMsg.name}
                      {bannerMsg.part && (<span className="event-chat-banner-part">（{bannerMsg.part}）</span>)}
                    </div>
                    <div className="event-chat-banner-preview">{bannerMsg.preview}</div>
                  </div>
                </div>
              )}

              {showNotes ? (
                <ArtistEventNotesPanel
                  eventId={selectedEventId}
                  onModeChange={(m) => {
                    const mode = typeof m === 'string' ? m : (m?.type || 'list');
                    notesModeRef.current = mode;
                    if (mode === 'detail') window.history.pushState({ artistView: 'note-detail' }, '');
                    if (selectedEventId) saveLast(selectedEventId, 'notes-list');
                  }}
                  onNotesChanged={refreshNoteCount} isReadOnly={isReadOnly}
                />
              ) : (
                <>
                  <div className="chat-messages chat-message-stack" ref={chatContainerRef}>
                    <EventMessageRows
                      messages={displayMessages}
                      unreadDividerMessageId={unreadDividerMessageId}
                      activeAnchorMessageId={activeAnchorMessageId}
                      cancelledRowProps={{
                        rowClassName: 'chat-message-wrapper align-center',
                        bodyClassName: 'message-body message-body--system',
                        contentRowClassName: 'message-content-row',
                        sourceBadgeClassName: 'message-source-badge',
                        timeClassName: 'message-time-label',
                        renderAnnouncementAvatarLeft,
                      }}
                      buildStandardRowProps={(msg) => ({
                        isAnnouncement: isAnnouncementMessage(msg),
                        isMyMessage: msg.isMyArtistMsg,
                        shouldSubtractSelfRead: true,
                        getMainPartLabel,
                        getAnnouncementSenderName,
                        bubbleClassByType: {
                          announcement: 'im-bubble admin-message note-card',
                          self: 'im-bubble artist-message',
                          other: 'im-bubble admin-message',
                        },
                        useFullWidthPartParentheses: false,
                        variant: 'plain',
                        sourceBadgeClassName: 'message-source-badge',
                        renderAnnouncementAvatarLeft,
                        renderAdminAvatarLeft,
                        renderArtistAvatarLeft,
                        onAvatarClick: !isAnnouncementMessage(msg) ? handleAvatarClick : undefined,
                        onContextMenu: inlineCopyMessageId ? undefined : (e) => handleMessageContextMenu(e, msg),
                        onTouchStart: inlineCopyMessageId ? undefined : (e) => handleMessageTouchStart(e, msg),
                        onTouchEnd: inlineCopyMessageId ? undefined : clearMessageLongPress,
                        onTouchCancel: inlineCopyMessageId ? undefined : clearMessageLongPress,
                        onTouchMove: inlineCopyMessageId ? undefined : clearMessageLongPress,
                        replyHeaderContent: msg.reply ? (
                          <ChatReplySnippet
                            reply={msg.reply}
                            onClick={() => handleJumpToReplyMessage(msg.reply)}
                          />
                        ) : null,
                        rowClassName:
                          inlineCopyMessageId && inlineCopyMessageId === String(msg?.id || '').trim()
                            ? 'chat-message-row--copyActive'
                            : inlineCopyMessageId && inlineCopyMessageId !== String(msg?.id || '').trim()
                              ? 'chat-message-row--copyDimmed'
                              : '',
                        bubbleClassName:
                          inlineCopyMessageId && inlineCopyMessageId === String(msg?.id || '').trim()
                            ? 'chat-message-row__bubbleWrap--copyActive'
                            : '',
                        renderMessageContent: (msg) =>
                          renderPartialCopyContent(msg, renderMessageContent(msg), !!msg.isMyArtistMsg),
                        sideActionContent:
                          !inlineCopyMessageId &&
                          !isAnnouncementMessage(msg) &&
                          !msg.isMyArtistMsg &&
                          supportsChatMessageReaction(msg) ? (
                            <ChatMessageReactionBar
                              messageId={msg.id}
                              reactions={msg.reactions}
                              canReact
                              showReactionChips={false}
                              onToggleReaction={toggleReaction}
                              onOpenDetail={openReactionDetail}
                              isPending={isReactionPending}
                              compact
                            />
                          ) : null,
                        metaEndContent:
                          !isAnnouncementMessage(msg) &&
                          Array.isArray(msg.reactions) &&
                          msg.reactions.length > 0 ? (
                            <ChatMessageReactionBar
                              messageId={msg.id}
                              reactions={msg.reactions}
                              canReact={false}
                              onOpenDetail={openReactionDetail}
                              isPending={isReactionPending}
                              showAddButton={false}
                            />
                          ) : null,
                      })}
                      resolveMessageSourceBadgeLabel={resolveMessageSourceBadgeLabel}
                      shouldShowMessageSourceBadge={shouldShowMessageSourceBadge}
                      formatDateTime={formatDateTimeJST}
                      withAnnouncementScopeTitle={withAnnouncementScopeTitle}
                      linkifyText={linkifyText}
                      shouldHideMessage={(msg) =>
                        msg?.message_type === 'system_message' && renderMessageContent(msg) == null
                      }
                    />
                    {loadingOlderMessages && hasMore && (
                      <div className="chat-paging-loading" aria-label="追加読み込み中">
                        <div className="chat-spinner chat-spinner--small" aria-hidden="true"></div>
                      </div>
                    )}
                    {shouldShowChatGuidance ? <ArtistEventChatGuidanceMessage /> : null}
                    {isUploading && (
                      <div className="chat-paging-loading" role="status" aria-live="polite">
                        アップロード中...
                      </div>
                    )}
                  </div>

                  {messageMenu && (
                    <div className="message-menu-overlay" onClick={closeMessageMenu}>
                      <div
                        ref={messageMenuRef}
                        className="message-menu"
                        style={{ top: messageMenu.y, left: messageMenu.x }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canReactMessage(messageMenu.message) ? (
                          <ChatMessageMenuReactionPicker
                            messageId={messageMenu.message?.id}
                            reactions={messageMenu.message?.reactions}
                            onSelectReaction={handleMessageMenuReactionSelect}
                            isPending={isReactionPending}
                          />
                        ) : null}
                        {canReplyMessage(messageMenu.message) ? (
                          <button
                            type="button"
                            className="message-menu__item"
                            onClick={handleSelectReplyMessage}
                          >
                            <i className="fa-solid fa-reply" aria-hidden="true" />
                            <span>返信する</span>
                          </button>
                        ) : null}
                        {canCopyMessage(messageMenu.message) ? (
                          <button
                            type="button"
                            className="message-menu__item"
                            onClick={handleCopyMessage}
                          >
                            <i className="fa-solid fa-copy" aria-hidden="true" />
                            <span>コピー</span>
                          </button>
                        ) : null}
                        {canCopyMessage(messageMenu.message) ? (
                          <button
                            type="button"
                            className="message-menu__item"
                            onClick={handleOpenInlinePartialCopy}
                          >
                            <i className="fa-solid fa-i-cursor" aria-hidden="true" />
                            <span>一部をコピー</span>
                          </button>
                        ) : null}
                        {canAnnouncementMessage(messageMenu.message) ? (
                          <button
                            type="button"
                            className="message-menu__item"
                            onClick={handleToggleAnnouncementMessage}
                          >
                            <i
                              className={`fa-solid ${
                                announcementActionMessageId === String(messageMenu.message?.id || '')
                                  ? 'fa-spinner fa-spin'
                                  : isAnnouncedMessage(messageMenu.message?.id)
                                    ? 'fa-bullhorn-slash'
                                    : 'fa-bullhorn'
                              }`}
                              aria-hidden="true"
                            />
                            <span>
                              {announcementActionMessageId === String(messageMenu.message?.id || '')
                                ? '処理中…'
                                : isAnnouncedMessage(messageMenu.message?.id)
                                  ? 'アナウンス解除'
                                  : 'アナウンスに設定'}
                            </span>
                          </button>
                        ) : null}
                        {canUnsendMessage(messageMenu.message) ? (
                          <button
                            type="button"
                            className="message-menu__item message-menu__item--danger"
                            onClick={handleUnsendMessage}
                          >
                            送信取り消し
                          </button>
                        ) : null}
                      </div>
                    </div>
                  )}

                  <ChatImageModal
                    isOpen={imgModalOpen && imageItems.length > 0}
                    src={imageItems[imgModalIndex]?.fullSrc}
                    downloadItems={imageItems}
                    currentIndex={imgModalIndex}
                    onClose={closeImageModal}
                    closeOnBack={false}
                    onPrev={showPrevImage}
                    onNext={showNextImage}
                    showNav={imageItems.length > 1}
                  />
                  {/* 入力（テキストはChatComposer／D&Dは直送） */}
                  {isSendLocked && (
                    <div className="send-locked-note" role="note">
                      イベント終了から1ヵ月が経過したため、アーティスト側からのメッセージ送信はできません。
                    </div>
                  )}
                  {/* ★ 下部固定用にページ固有クラス aecp-composer-fixed を追加 */}
                  <div className={`composer-guard aecp-composer-fixed${isSendLocked ? ' is-locked' : ''}`} aria-disabled={isSendLocked ? 'true' : 'false'}>
                    <ChatComposer
                      ref={composerRef}
                      mode="artist"
                      scope="event"
                      eventId={selectedEventId}
                      senderId={loggedInArtistId}
                      canSendFiles={!isSendLocked}
                      blockedMessage={sendBlockedMessage}
                      placeholderText="メッセージを入力…"
                      onUploading={handleUploading}
                      replyDraft={replyDraft}
                      onReplyCancel={() => setReplyDraft(null)}
                      mentionCandidates={mentionCandidates}
                      readOnly={isReadOnly || isSendLocked}
                      onSendBlocked={(msg) => {
                        toastError(msg || sendBlockedMessage);
                      }}
                      onSent={async (summary) => {
                        if (isReadOnly) {
                          toastError('現在メンテナンス中のため送信できません。');
                          return;
                        }
                        const preview =
                          summary?.type === 'images' ? '画像が届いています。' :
                          summary?.type === 'file'  ? 'ファイルが届いています。' :
                          (summary?.text || '');
                        if (preview) updateSidebarAfterSend(preview);

                        await fetchNewMessages();
                        refreshPendingMentionTargets();
                        setReplyDraft(null);
                        requestAnimationFrame(() => {
                          if (chatContainerRef.current) chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        });
                      }}
                      onError={(err) => {
                        const msg = err?.message ? normalizeServerMessage(err.message) : null;
                        toastError(msg || '送信に失敗しました。時間を置いて再度お試しください。');
                      }}
                      onExternalFiles={() => false}
                    />
                  </div>
                </>
              )}

              <ArtistMemberListModal
                isOpen={isMemberModalOpen}
                onClose={() => setIsMemberModalOpen(false)}
                eventId={selectedEventId}
                partsList={partsList}
                assignedAdmins={selectedEventAdmins}
                conciergePlanner={conciergePlanner}
                isConciergeEvent={hasConciergeParticipants}
                isUnifiedEventGroupChat={isSelectedGroupUnified}
                membersVisible={membersVisibleToArtists}
                currentEventMembersVisible={currentEventMembersVisibleToArtists}
              />
              <ChatProfileModalLayer
                profileTarget={profileTarget}
                onClose={closeProfileModal}
                variant="artist"
                allowArtistLink={false}
              />
                <ChatAnnouncementListModal
                  open={isAnnouncementListOpen}
                  announcements={announcements}
                  onClose={() => setIsAnnouncementListOpen(false)}
                  onRemove={unpinAnnouncement}
                  removingMessageId={announcementActionMessageId}
                  onSelect={handleSelectAnnouncementMessage}
                />
              <ArtistEventMaterialsModal
                isOpen={showMaterialsModal}
                eventId={selectedEventId}
                canUpload={!isReadOnly}
                onClose={() => setShowMaterialsModal(false)}
              />
              <ArtistEventAlbumModal
                isOpen={showAlbumModal}
                eventId={selectedEventId}
                eventName={selectedEventInfo?.name || ''}
                eventDateLabel={eventHeaderSchedule?.date || ''}
                onClose={() => setShowAlbumModal(false)}
              />
              <ChatLinksModal
                isOpen={showLinksModal}
                loadLinks={chatAssetSource?.loadLinks}
                onClose={() => setShowLinksModal(false)}
              />
              <ChatMediaModal
                isOpen={showMediaModal}
                loadMedia={chatAssetSource?.loadMedia}
                downloadZip={chatAssetSource?.downloadZip} buildFileUrl={chatAssetSource?.buildFileUrl}
                onClose={() => setShowMediaModal(false)}
              />
              <ChatMessageReactionDetailModal
                  open={isReactionDetailOpen}
                loading={reactionDetailLoading}
                groups={reactionDetailGroups}
                onClose={closeReactionDetail}
              />
            </>
          )}
          </div>

          <ArtistEventChatSidePanel
            isVisible={Boolean(selectedEventId && selectedEventInfo)}
            isOpen={isSidePanelOpen}
            onCollapse={() => setIsSidePanelOpen(false)}
            onExpand={() => setIsSidePanelOpen(true)}
            eventInfoProps={{
              selectedEventInfo,
              selectedEventFlyerUrl,
              selectedEventFlyerBackUrl,
              selectedEventFlyerFullUrl,
              selectedEventFlyerBackFullUrl,
              onFlyerImageLoad: applyLandscapeContain,
              isSelectedGroupUnified,
              groupEventDetailRows,
              resolveEventDetailPath: (eventUuid) => `/artist/workspaces/event-operations/events/${eventUuid}`,
              eventSchedule,
              scheduleDetail,
              venueLink,
              venueId: venueIdFromData,
              venueDetailBasePath: '/artist/workspaces/event-operations/venues',
              venueResolvedName,
              venueInfo,
              mapLinkUrl,
              venueResolvedAddress,
              sidebarNote,
              eventDetailUrl,
            }}
            participantsPanelProps={participantsPanelProps}
          />
        </div>
        </ChatWorkspaceLayout>
        <ChatMentionTaskResolutionModal {...mentionResolutionModalProps} />
      </div>
    </div>
  );
}
